/**
 * CompareKeywordPage — keyword comparison.
 *
 * One A/B-style strip of up to 100 side-by-side columns (A-E, then 6...), filled
 * lazily as they scroll into view. The ★ "shortlist" is a SEPARATE set: star
 * drugs in the rail, see them in the Starred tab, and save them as a list.
 * URL: ?panels/?cols (columns), ?stars= (shortlist), ?kw=,
 * ?section=; ?view=stars is a legacy link, migrated on load. Each column reuses
 * the drug-detail viewers per corpus (SPL / CA·AU PDF / SBA·EPAR·letter),
 * auto-scrolled to + word-highlighting the
 * keyword. Export ▾ → offline HTML / Excel / Word, side-by-side, keyword highlighted.
 * Select text in a column → "Highlight" (manual, page memory only) — shown on
 * screen and carried into the Export files (see UserHighlighter).
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
  type DragEvent, type ReactNode, type Ref, type UIEvent,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { usePublishChatContext } from '../shell/chatContext';
import { useTab } from '../shell/TabContext';
import SplContentRenderer, { highlightHtmlString, recolorHighlightedHtml } from '../components/SplContentRenderer';
import SBAViewerEmbed from '../components/viewer/SBAViewerEmbed';
import EPARViewerEmbed from '../components/viewer/EPARViewerEmbed';
import CAPMViewerEmbed from '../components/viewer/CAPMViewerEmbed';
import AUViewerEmbed from '../components/viewer/AUViewerEmbed';
import UKPILViewerEmbed from '../components/viewer/UKPILViewerEmbed';
import EUViewerEmbed from '../components/viewer/EUViewerEmbed';
import ChPdfViewerEmbed from '../components/viewer/ChPdfViewerEmbed';
import DePdfViewerEmbed from '../components/viewer/DePdfViewerEmbed';
import FrHtmlViewer from '../components/viewer/FrHtmlViewer';
import FdaLabelPdfViewerEmbed from '../components/viewer/FdaLabelPdfViewerEmbed';
import FdaLetterPageViewer from '../components/fdaLetter/FdaLetterPageViewer';
import JPBilingualSections, {
  type JPDoc,
  type JPSection,
  type JPFigure,
  type JPLang,
} from '../components/viewer/JPBilingualSections';
import { type JPTemplateEntry } from '../components/viewer/JPSectionView';
import { regionDisplayLabel, isUsFdaVisible } from '../constants/filterConstants';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useDrugQuery } from '../hooks/useDrugQuery';
import { compareBackLabel, findSectionBlock, matchesRailFilter, panelVersionLine, railCount, topicEntries, type FilterMeta } from '../utils/compareView';
import { useKeywordSearchInfinite } from '../hooks/useKeywordSearch';
import { kwSearchIdentity } from './keywordRequest';
import { CAPTURE_MAX_ROWS } from '../shell/runResults';
import { useSearchQueryV2Drugs } from '../hooks/useSearchQueryV2Drugs';
import { buildDrugHighlightParams, isEnglishExactSource } from '../utils/keywordDeepLink';
import { buildColoredTerms } from '../utils/highlightColors';
import { keywordSearchTerms, proximityOperands } from '../utils/keywordQueryText';
import { ACTIVE_EXPORT_STATUSES, exportJobsApi, type EstimateItem, type ExportEstimate, type ExportGatherSpec, type ExportJobStatus } from '../services/exportJobsApi';
import { gzipJson, uploadPayload, type ExportPayload } from '../utils/exportPayload';
import { pagesToEmbed } from '../utils/pdfPageImages';
import HighlightLegend from '../components/HighlightLegend';
import { LazyMount } from '../components/LazyMount';
import UserHighlighter, {
  UserHighlightsChip,
  USER_HIGHLIGHT_DRUG_ATTR,
  USER_HIGHLIGHT_IGNORE_ATTR,
  useUserHighlights,
} from '../components/UserHighlighter';
import { SaveStarredListButton } from '../components/SavedLists';
import UniversalSectionSelect from '../components/UniversalSectionSelect';
import { useSectionConcepts } from '../hooks/useSectionConcepts';
import { groupRows, type GroupByOption } from '../utils/groupResults';
import { formatRefineChipValue, parseRefineParam, REFINE_KIND_LABELS } from '../utils/refineQuery';
import { filterByRefineChips, KW_SORT_PARAM, parseSortParam } from '../utils/keywordViewParams';
import { COGNI_TABLE_FILTER_PARAMS, cogniTableFilterLabels, matchesCogniTableFilters, parseCogniTableFilters } from '../utils/cogniTableFilters';
import RailBulkStarBar from '../components/RailBulkStarBar';
import {
  applyBulkStar,
  applyBulkUnstar,
  describeBulkStar,
  selectAllUpTo,
  selectionCapNote,
  toggleSelected,
} from '../utils/bulkStar';
import {
  expandSectionFamilies,
  narrowedSections,
  resolveColumnConcepts,
    type ConceptEntry,
} from '../utils/universalSections';
import type { SavedListDrug, SearchDescriptor } from '../types';
import type { TOCEntry } from '../types/viewer';
import SectionTreeSelect, { effectiveSelection, sectionNumbersOf } from '../components/SectionTreeSelect';
import { authApi } from '../services/authApi';
import { sbaApi, eparApi, caPmApi, auApi, ukPilApi, euApi, chPdfApi, dePdfApi, fdaLabelPdfApi, type ViewerApi } from '../api/createViewerApi';
import { downloadCompareHtml, downloadCompareXls, type ExportDrug } from '../utils/compareExport';
import {
  REGION_DOC_SOURCE,
  applyUserHighlights,
  gatherExportDrugs as gatherDrugs,
  hitFromResult,
  sectionLabel,
  sectionsFor,
  viewerPrefixFor,
  type GatherClient,
  type GatherContext,
} from '../utils/exportGather';
import { EXPORT_STAGE_LABEL, ExportStageTimer, isAbortError, stageForPercent, stagePercent } from '../utils/exportProgress';
import type {
  DrugResultV2,
  KeywordMatchHighlight,
  KeywordSearchRequest,
  KeywordSearchResponse,
  KeywordSearchResultItem,
  SBASummaryForDrug,
  SectionResponse,
} from '../types';

// Per-label identity colors, by panel position (slot A-E). The compare panels
// and the matching left-rail rows share these so the user can tell which rail
// drug maps to which panel (A6 board).
const PANEL_COLORS = ['#14746D', '#9E3B2F', '#1B2622', '#9A6A12', '#5B4B8A'];
// ★ shortlist cap, and the column cap: one export holds at most 100 drugs.
const MAX_STARS = 100; // export/shortlist cap (Req 3); lazy-mount keeps 100 columns responsive
const MAX_PANELS = MAX_STARS;
// Slots 1-5 keep A-E; slot 6+ shows its 1-based number. Colours cycle the palette.
const slotLabel = (i: number) => (i < 5 ? 'ABCDE'[i]! : String(i + 1));
const slotColor = (i: number) => PANEL_COLORS[i % PANEL_COLORS.length]!;
// Rail paging while Refine chips from the results page are active (2.4): larger
// pages, and auto-fill stops after the results page's own page size.
const FILTERED_RAIL_PAGE_SIZE = 50;
const FILTERED_RAIL_AUTO_ROWS = 200;
// Gold ★ colour (rail star icon).
const STAR_GOLD = '#C98A1B';

// Stop words dropped when deriving highlight terms from a free-text Cogni query.
const COGNI_STOP_WORDS = new Set([
  'a', 'an', 'the', 'of', 'in', 'for', 'and', 'or', 'to', 'is', 'with', 'on',
  'at', 'by', 'as', 'are', 'was', 'be', 'it', 'this', 'that', 'from',
]);

/** Meaningful, deduped, lowercased words from a Cogni query string. */
function cogniTerms(q: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of q.split(/\s+/)) {
    const w = raw.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
    if (w.length < 3 || COGNI_STOP_WORDS.has(w) || seen.has(w)) continue;
    seen.add(w);
    out.push(w);
  }
  return out;
}

// Imperative handle exposed by every regulatory viewer embed.
export type ViewerHandle = {
  scrollToPage: (n: number) => void;
  scrollToSection: (
    s: string,
    opts?: { allowSubsectionPrefix?: boolean; deferUntilLoaded?: boolean },
  ) => boolean | void;
};

export function regulatoryViewer(
  source: string,
  docId: number,
  chunkIds: number[],
  term: string,
  terms: string[],
  colors: (string | undefined)[],
  ref?: Ref<ViewerHandle>,
  // Compare view "show only this section's pages": the section numbers to keep
  // (resolved to pages inside the embed), plus a TOC callback so the column can
  // build its own section picker. Both optional — other callers omit them.
  visibleSectionNumbers?: string[] | undefined,
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined,
  englishExact = false,
): ReactNode {
  // Box every query term in the PDF text layer (not just the first), so a
  // multi-row keyword search highlights all of them — each row in its color.
  const common = {
    docId,
    highlightChunkIds: chunkIds,
    highlightTerm: term,
    highlightTerms: terms.length ? terms : null,
    highlightColors: colors.length ? colors : null,
    visibleSectionNumbers,
    onTocLoaded,
    englishExact,
  };
  // ref must be passed explicitly (React strips it from spread props).
  if (source === 'sba') return <SBAViewerEmbed ref={ref as never} {...common} />;
  if (source === 'epar') return <EPARViewerEmbed ref={ref as never} {...common} />;
  if (source === 'ca_pm') return <CAPMViewerEmbed ref={ref as never} {...common} />;
  if (source.startsWith('au')) return <AUViewerEmbed ref={ref as never} {...common} />;
  if (source === 'uk_pil') return <UKPILViewerEmbed ref={ref as never} {...common} />;
  if (source === 'eu') return <EUViewerEmbed ref={ref as never} {...common} />;
  if (source === 'ch_pdf') return <ChPdfViewerEmbed ref={ref as never} {...common} />;
  if (source === 'de_pdf') return <DePdfViewerEmbed ref={ref as never} {...common} />;
  // Irish (English) / Italian label PDFs: the drug page's viewer with the family set.
  if (source === 'ie_pdf' || source === 'it_pdf') return <DePdfViewerEmbed ref={ref as never} family={source} {...common} />;
  if (source === 'fda_label_pdf') return <FdaLabelPdfViewerEmbed ref={ref as never} {...common} />;
  return null;
}

// Non-US regulatory corpus per region: the regulatoryViewer `source` it maps
// to + the authApi fetch for the drug's regional LABEL document. A Cogni/`label`
// result for a CA/AU/UK drug carries no document_id, so we resolve the drug's
// primary regional label PDF here (exactly like DrugDetailPage's
// fetchDrugCAPMs/AUDocs/UKPILs, which each pick documents[0]) and render the
// matching PDF viewer instead of the mangled section text.
//
// EU now renders the EMA SmPC PDF (the actual EU LABEL), not the EPAR.
// Historically EU was excluded here because its only stored PDF was the EPAR
// (`epar_public_assessment` / `smop`) — an HA-Review ASSESSMENT REPORT, NOT the
// label — so a `label` match fell through to section text. With the eu_* SmPC
// migration the EU main label is now a real page-backed PDF in `eu_documents`
// (served by the `/eu` viewer family, identical contract to CA/AU/UK), so a
// `label` match for an EU drug resolves its primary SmPC via getDrugEUDocs and
// renders EUViewerEmbed. Drugs not yet backfilled return zero documents and
// RegionalPdfColumn falls back to the section-text panel automatically.
export const REGION_DOC_CONFIG: Record<
  string,
  { source: string; fetch: (drugId: number) => Promise<{ documents: SBASummaryForDrug[] }> }
> = {
  CA: { source: REGION_DOC_SOURCE['CA']!, fetch: (id) => authApi.getDrugCAPMs(id) },
  AU: { source: REGION_DOC_SOURCE['AU']!, fetch: (id) => authApi.getDrugAUDocs(id) },
  UK: { source: REGION_DOC_SOURCE['UK']!, fetch: (id) => authApi.getDrugUKPILs(id) },
  EU: { source: REGION_DOC_SOURCE['EU']!, fetch: (id) => authApi.getDrugEUDocs(id) },
  // Swiss / German label PDFs: the SmPC (Fachinformation) first — the list also
  // carries the patient leaflet.
  CH: { source: REGION_DOC_SOURCE['CH']!, fetch: (id) => authApi.getDrugChPdfDocs(id).then(smpcFirst) },
  DE: { source: REGION_DOC_SOURCE['DE']!, fetch: (id) => authApi.getDrugDePdfDocs(id).then(smpcFirst) },
  // Irish (HPRA) / Italian (AIFA) label PDFs, SmPC first. Literal sources: they are
  // deliberately not in exportGather's REGION_DOC_SOURCE (IE/IT export is not built).
  IE: { source: 'ie_pdf', fetch: (id) => authApi.getDrugLabelPdfDocs(id, 'ie_pdf').then(smpcFirst) },
  IT: { source: 'it_pdf', fetch: (id) => authApi.getDrugLabelPdfDocs(id, 'it_pdf').then(smpcFirst) },
  // US_FDA identity (us_fda_drug_id), not the legacy /fda_label_pdf_docs drug_id match.
  US_FDA: {
    source: REGION_DOC_SOURCE['US_FDA']!,
    fetch: async (id) => {
      const labelId = await authApi.getUsFdaCurrentLabelId(id);
      return { documents: labelId == null ? [] : [{ id: labelId } as SBASummaryForDrug] };
    },
  },
};

/** Main label (SmPC) before the patient leaflet; order otherwise kept. */
function smpcFirst<T extends { documents?: { doc_type?: string | null }[] }>(d: T): T {
  const docs = [...(d?.documents ?? [])];
  docs.sort((a, b) => Number(b.doc_type === 'smpc') - Number(a.doc_type === 'smpc'));
  return { ...d, documents: docs };
}

/** Keyword corpora that are a region's LABEL (keyword highlights carry the
 *  corpus key as ``source``, not 'label' — same as JP's 'jp'). */
export const REGION_LABEL_SOURCES = new Set(['label', 'ch', 'ch_pil', 'ch_src', 'de', 'fr', 'ie', 'it']);

/** France (fr_html): an HTML label, not a PDF — the panel renders the same
 *  FrHtmlViewer as the drug page (its own TOC + English/Français toggle) for
 *  the drug's current RCP. */
function FrCompareColumn({ drugId, fallback }: { drugId: number; fallback: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['regional-doc', 'FR', drugId],
    queryFn: () => authApi.getFrHtmlDocs(drugId),
    enabled: !!drugId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  if (isLoading) {
    return <div className="flex h-full items-center justify-center text-sm text-ink-3">Loading document…</div>;
  }
  const doc = data?.documents?.[0];
  if (!doc) return <>{fallback}</>;
  return <FrHtmlViewer key={doc.id} docId={doc.id} updated={doc.updated_source ?? null} />;
}

/** A non-US (CA/AU/EU/UK) drug whose keyword/Cogni match came via the `label`
 *  source (no document_id) — resolve its primary regional document and render
 *  the regulatory PDF viewer, like DrugDetailPage does. Falls back to the
 *  caller-supplied section-text node while loading / when no doc exists, so the
 *  panel is never empty. */
function RegionalPdfColumn({
  drugId,
  region,
  viewerRef,
  phrase,
  terms,
  colors,
  fallback,
  evidenceText,
  evidenceSection,
  englishExact,
  visibleSectionNumbers,
  onTocLoaded,
}: {
  drugId: number;
  region: string;
  viewerRef: Ref<ViewerHandle>;
  phrase: string;
  terms: string[];
  colors: (string | undefined)[];
  fallback: ReactNode;
  englishExact?: boolean;
  /** Compare: section numbers to keep (page-filter), resolved to pages in the embed. */
  visibleSectionNumbers?: string[] | undefined;
  /** Compare: fires when the regional PDF's TOC loads (for the section picker). */
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
  /** Cogni: the AI evidence chunk text — trigram-matched server-side to
   *  a stored chunk so the viewer can box + scroll to it (same path as
   *  keyword `highlightChunkIds`). */
  evidenceText?: string | null | undefined;
  /** Cogni: the evidence section number — tiebreak boost for the chunk
   *  resolve (prefers a chunk in that section / subsection). */
  evidenceSection?: string | null | undefined;
}) {
  const cfg = REGION_DOC_CONFIG[region];
  const { data, isLoading } = useQuery({
    queryKey: ['regional-doc', region, drugId],
    queryFn: () => cfg!.fetch(drugId),
    enabled: !!cfg && !!drugId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const docId = data?.documents?.[0]?.id;
  // Resolve the Cogni evidence snippet to a boxable chunk id on this
  // document. Trigram match server-side; null when nothing clears the
  // similarity threshold (then the text-layer term highlight is the
  // only highlight, which is fine). Keyed on the snippet so a different
  // evidence chunk re-resolves.
  const regApi = cfg ? regApiFor(cfg.source) : null;
  const { data: resolvedChunkId } = useQuery({
    queryKey: ['resolve-chunk', cfg?.source, docId, evidenceText],
    queryFn: () => regApi!.resolveChunk(docId!, evidenceText!, evidenceSection ?? null),
    enabled: !!regApi && docId != null && !!evidenceText,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  // No config (shouldn't happen — caller gates on region) → section text.
  if (!cfg) return <>{fallback}</>;
  if (isLoading) {
    return <div className="flex h-full items-center justify-center text-sm text-ink-3">Loading document…</div>;
  }
  // No regional document found → keep the section-text rendering (never empty).
  if (docId == null) return <>{fallback}</>;
  return (
    <>
      {regulatoryViewer(
        cfg.source,
        docId,
        resolvedChunkId != null ? [resolvedChunkId] : [],
        terms.length ? terms[0]! : phrase,
        terms,
        colors,
        viewerRef,
        visibleSectionNumbers,
        onTocLoaded,
        englishExact,
      )}
    </>
  );
}

/** A JP (region='JP') drug's main label.  The PMDA package insert lives in the
 *  separate ``jp_sections`` family (the drug's main ``sections`` are empty), and
 *  it's bilingual.  So a JP Compare panel renders the SAME bilingual accordion as
 *  the drug-detail JP viewer, but fed from a per-panel fetch of
 *  ``/drugs/{id}/jp-sections`` (exactly like RegionalPdfColumn fetches a CA/AU/UK
 *  regional doc per panel) and with its OWN independent EN/日本語 toggle in the
 *  panel header.  English is the default.  JP-only — never touches US/EU panels.
 *
 *  ``lang`` + ``setLang`` are LIFTED to the panel so the toggle can sit in the
 *  shared panel header next to the section dropdown (the body has no sticky bar
 *  of its own — the panel already scrolls). */
function JPCompareColumn({
  drugId,
  lang,
  setLang,
  selectedSections,
  highlightTerms,
  highlightColors,
  englishExact,
  evidenceChunkText,
  scrollToSectionNumber,
}: {
  drugId: number;
  lang: JPLang;
  setLang: (l: JPLang) => void;
  /** Per-panel section selection (the JP section_numbers to SHOW); undefined =
   *  all.  Bound to this drug's OWN JP sections by the panel header dropdown.
   *  MANUAL selection only — the evidence/concept section is a SCROLL TARGET
   *  (below), never a filter, so the panel shows the whole label like every
   *  other region. */
  selectedSections: string[] | undefined;
  /** Keyword: per-row colored terms to box.  Cogni: empty (the blue evidence
   *  block carries the highlight instead). */
  highlightTerms?: string[];
  highlightColors?: (string | undefined)[];
  englishExact?: boolean;
  /** Cogni: the AI evidence chunk — blue `.evidence-chunk-highlight` block. */
  evidenceChunkText?: string | null;
  /** The section to scroll to once content is laid out (Cogni evidence section;
   *  null for keyword, which scrolls to the first <mark>). */
  scrollToSectionNumber?: string | null;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['jp-sections', drugId],
    queryFn: () => authApi.getDrugJPSections(drugId) as Promise<{
      document: JPDoc | null;
      sections: JPSection[];
      figures?: JPFigure[];
      template?: JPTemplateEntry[];
    }>,
    enabled: !!drugId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  // Each top-level section starts expanded so the side-by-side comparison shows
  // content immediately (Compare has no Contents dropdown to drive expansion).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const seeded = useRef(false);
  const sections = data?.sections ?? [];
  useEffect(() => {
    if (seeded.current || sections.length === 0) return;
    seeded.current = true;
    setExpanded(new Set(sections.map((s) => s.section_number)));
  }, [sections]);
  const toggle = (num: string) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(num) ? n.delete(num) : n.add(num);
      return n;
    });

  // Scroll ONCE per drug to the evidence: the blue chunk block, else the first
  // term <mark>, else the evidence section heading (fallback so the panel still
  // lands on the right place). Mirrors DrugColumn's section-text scroll effect.
  const scrollBox = useRef<HTMLDivElement>(null);
  const didScroll = useRef(false);
  useEffect(() => { didScroll.current = false; }, [drugId]);
  const hasHL = (highlightTerms && highlightTerms.length > 0) || !!evidenceChunkText;
  useEffect(() => {
    if (didScroll.current) return;
    if (!hasHL && !scrollToSectionNumber) return;
    if (sections.length === 0) return;
    let tries = 0;
    const iv = setInterval(() => {
      const c = scrollBox.current;
      if (!c) { if (++tries > 40) clearInterval(iv); return; }
      const hit =
        c.querySelector('.evidence-chunk-highlight') ??
        c.querySelector('mark') ??
        (scrollToSectionNumber ? c.querySelector(`[data-section="${scrollToSectionNumber}"]`) : null);
      if (hit) {
        c.scrollTop += (hit as HTMLElement).getBoundingClientRect().top - c.getBoundingClientRect().top - 48;
        didScroll.current = true;
        clearInterval(iv);
        return;
      }
      if (++tries > 40) clearInterval(iv);
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drugId, hasHL, scrollToSectionNumber, evidenceChunkText, sections.length, lang]);

  if (isLoading) {
    return <div className="flex h-full items-center justify-center text-sm text-ink-3">Loading Japanese label…</div>;
  }
  if (!data?.document || sections.length === 0) {
    return <div className="flex h-full items-center justify-center text-sm text-ink-3">No Japanese package-insert sections found.</div>;
  }
  return (
    <div ref={scrollBox} className="flex-1 overflow-y-auto px-4 py-3">
      <JPBilingualSections
        doc={data.document}
        sections={sections}
        figures={data.figures ?? []}
        expanded={expanded}
        onToggle={toggle}
        withSectionAnchors={false}
        stickyToggle={false}
        hideDocHeader
        lang={lang}
        englishExact={!!englishExact}
        onLangChange={setLang}
        {...(highlightTerms ? { highlightTerms } : {})}
        {...(highlightColors ? { highlightColors } : {})}
        {...(evidenceChunkText ? { evidenceChunkText } : {})}
        {...(selectedSections ? { sectionFilter: selectedSections } : {})}
      />
    </div>
  );
}

/** Section-tree options for a JP panel's per-panel dropdown.  MERGES the full
 *  canonical PMDA ``template`` (every top-level §N, present or not) with the
 *  drug's present ``sections`` (the only source of subsections), shaped like
 *  ``SectionResponse`` so SectionTreeSelect consumes them unchanged:
 *   - each template §N → selectable when ``present``, else greyed + non-selectable
 *     (``absent: true``, no content) so the numbering reads continuously;
 *   - each present §N.M subsection → selectable, nesting under its parent.
 *  Top-level present sections come ONLY from the template (avoid duplicate rows).
 *  This mirrors the JP drug-detail TOC, which greys absent canonical sections. */
function jpSectionOptions(
  template: JPTemplateEntry[] | undefined,
  sections: JPSection[] | undefined,
): SectionResponse[] {
  const out: SectionResponse[] = [];
  let id = 1;
  for (const t of template ?? []) {
    const title = t.title_en || t.title_ja || `Section ${t.section_number}`;
    out.push({
      id: id++,
      loinc_code: null,
      section_type: null,
      section_number: t.section_number,
      title,
      // Present → seed content_text with the title so SectionTreeSelect keeps it
      // selectable (it carries no real body here; never displayed). Absent → no
      // content + absent flag → greyed, non-selectable, never counted.
      content_text: t.present ? title : null,
      content_xml: null,
      ...(t.present ? {} : { absent: true }),
    });
  }
  // Subsections (§N.M) come only from the present sections; the template is
  // top-level only. Skip top-level entries — the template already provides them.
  for (const s of sections ?? []) {
    if (!s.section_number || !s.section_number.includes('.')) continue;
    const title = s.title || s.title_ja || `Section ${s.section_number}`;
    out.push({
      id: id++,
      loinc_code: null,
      section_type: s.section_type ?? null,
      section_number: s.section_number,
      title,
      content_text: title,
      content_xml: null,
    });
  }
  return out;
}

/** Section titles are part of the FTS search_vector (title + body), so a keyword
 *  CAN match in a header — highlight it there too, with the same per-row colors
 *  as the body. Returns highlighted HTML for the header label. */
function highlightLabel(label: string, terms: string[], colors: (string | undefined)[], englishExact = false): string {
  const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return englishExact ? highlightHtmlString(escaped, terms, colors, null, { englishExact }) : recolorHighlightedHtml(escaped, terms, colors);
}

/** Viewer API for a regulatory source (the family prefix comes from the
 *  shared export gather's mapping). */
const VIEWER_BY_PREFIX: Record<string, ViewerApi> = {
  '/sba': sbaApi, '/epar': eparApi, '/ca_pm': caPmApi, '/au': auApi, '/uk_pil': ukPilApi, '/eu': euApi,
  '/ch_pdf': chPdfApi, '/de_pdf': dePdfApi,
  '/fda_label_pdf': fdaLabelPdfApi,
};
function regApiFor(source: string): ViewerApi | null {
  const prefix = viewerPrefixFor(source);
  return prefix ? VIEWER_BY_PREFIX[prefix] ?? null : null;
}

/** The export gather's reads, through the BFF with the user's token. */
const browserGatherClient: GatherClient = {
  getDrug: (id) => authApi.getDrug(id),
  getDrugJPSections: (id) => authApi.getDrugJPSections(id),
  getFDALetterSectionPages: (sectionId) => authApi.getFDALetterSectionPages(sectionId),
  regionDocs: (region, id) => REGION_DOC_CONFIG[region]!.fetch(id),
  viewer: (prefix) => VIEWER_BY_PREFIX[prefix]!,
};

type ExportFormat = 'html' | 'xls' | 'xlsx' | 'docx';

/** The downloader for an Export format. Word + Excel are lazy-loaded so their
 *  libraries only ship when used. 'xls' is the older HTML-as-Excel export —
 *  kept, but the menu now offers the real .xlsx instead. */
async function exportDownloader(format: ExportFormat) {
  switch (format) {
    case 'html': return downloadCompareHtml;
    case 'xls': return downloadCompareXls;
    case 'xlsx': return (await import('../utils/compareExportXlsx')).downloadCompareXlsx;
    case 'docx': return (await import('../utils/compareExportDocx')).downloadCompareDocx;
  }
}

// ─── one panel/column (the compare strip) ──────────────────────────
function DrugColumn({
  drugId,
  result,
  sectionNumber,
  selectedSections,
  onSectionsChange,
  concepts,
  phrase,
  terms,
  colors,
  englishExact,
  evidenceChunkText,
  scrollToSectionNumber,
  onOpen,
  onRemove,
  onReorderDrop,
  onDropDrug,
  accentColor,
  slotLetter,
  universalPicks,
  onClearSections,
}: {
  drugId: number;
  result: KeywordSearchResultItem | null;
  sectionNumber: string;
  // Per-column hierarchical section selection (the section_numbers to SHOW).
  // undefined = all sections (default).
  selectedSections: string[] | undefined;
  onSectionsChange: (next: string[]) => void;
  // The active universal section(s) (or, for Cogni, a synthetic one-region
  // concept built from the evidence). The column resolves each to ITS OWN
  // region and UNIONS the families: a section-text column narrows to that
  // union, a PDF column shows only those sections' pages (scrolling to the
  // first), and a region a concept does not exist in keeps its whole document
  // and shows the note instead. Overridden by a manual selectedSections.
  concepts?: ConceptEntry[];
  phrase: string;
  // Ordered query-row terms + their per-row colors (multi-row AND/OR queries).
  // Falls back to [phrase] when there's only the single URL keyword.
  terms: string[];
  colors: (string | undefined)[];
  englishExact?: boolean;
  /** Cogni: the AI evidence chunk to highlight + scroll to, via
   *  SplContentRenderer's anchor-based evidence-chunk-highlight — the SAME
   *  mechanism the drug-detail view uses (robust fuzzy match, not exact). */
  evidenceChunkText?: string | null;
  /** Cogni: the evidence section number — scroll fallback when the chunk
   *  highlight doesn't anchor-match, so the panel still lands on the section. */
  scrollToSectionNumber?: string | null;
  onOpen: (drugId: number) => void;
  onRemove?: (drugId: number) => void;
  onReorderDrop?: (fromId: number, beforeId: number) => void;
  onDropDrug?: (drugId: number) => void;
  /** Per-label identity color (PANEL_COLORS by slot). Drives the 3px top border + the slot badge; the matching rail row
   *  shares the same color. */
  accentColor?: string | undefined;
  /** Slot label for the header badge (A-E, then 6…). */
  slotLetter?: string | undefined;
  /** The page's `?usec=` picks, independent of the flag-dependent per-panel
   *  narrowing: the topic line reports where each pick landed in THIS panel. */
  universalPicks?: ConceptEntry[] | undefined;
  /** Drops this drug's manual section pick (format switch, R7.5). */
  onClearSections?: (() => void) | undefined;
}) {
  const { data: drug, isLoading } = useDrugQuery(String(drugId), { enabled: !!drugId });
  const usFdaVisible = isUsFdaVisible(useIsAdmin());

  const primary: KeywordMatchHighlight | null = result?.highlights?.[0] ?? null;
  const source = String(primary?.source ?? 'label');
  const region = drug?.region || 'US';
  // A non-US (CA/AU/EU/UK) drug matched via the `label` source carries no
  // document_id, and its structured `sections` text is mangled PDF extraction
  // (worst in Cogni). Render its regulatory PDF viewer instead — resolved from
  // the drug's regional document by RegionalPdfColumn, exactly like the
  // drug-detail page does. US `label` drugs keep section-text rendering, and an
  // actual regulatory-source match (ca_pm / au_pi / epar / uk_pil / sba, which
  // carries a document_id) is unchanged below.
  const isNonUsLabel = REGION_LABEL_SOURCES.has(source) && region !== 'US' && !!REGION_DOC_CONFIG[region];
  const isFr = region.toUpperCase() === 'FR' && REGION_LABEL_SOURCES.has(source);
  // JP (region='JP'): the PMDA package insert lives in the separate bilingual
  // ``jp_sections`` family (the drug's main ``sections`` are empty), so a JP
  // panel renders the bilingual JP accordion (its OWN EN/日本語 toggle) via
  // JPCompareColumn instead of the section-text panel.  Gated on REGION only —
  // a JP drug has no PDF viewer, so it must use the accordion whether the match
  // arrived via the Cogni `label` source OR the keyword `jp` source (keyword
  // highlights carry source='jp', not 'label' — see hl_jp_sql).  Gating on
  // `source === 'label'` blocked keyword matches and left the panel blank
  // (regulatoryViewer has no `jp` case → null).  US/EU/CA/AU/UK unchanged.
  const isJp = region.toUpperCase() === 'JP';
  // `sectionsBacked` drives the on-screen section-text panel + its scroll/
  // highlight effects. A non-US `label` drug renders a PDF instead, so it is NOT
  // sections-backed.  JP renders its own bilingual accordion → also NOT
  // sections-backed.
  const sectionsBacked = source === 'label' && !isNonUsLabel && !isJp && !isFr;
  // PDF | Text (R7). Same query key as RegionalPdfColumn, so no second request.
  const docCfg = REGION_DOC_CONFIG[region];
  const { data: docData, isPending: docPending } = useQuery({
    queryKey: ['regional-doc', region, drugId],
    queryFn: () => docCfg!.fetch(drugId),
    enabled: isNonUsLabel && !!drugId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const hasDoc = docData?.documents?.[0]?.id != null;
  // Content-bearing = `sectionsFor`'s filter (non-empty trimmed text or XML).
  const hasText = useMemo(() => sectionsFor(drug, '').length > 0, [drug]);
  // The settled no-document case (incl. a failed query): RegionalPdfColumn would
  // render section text here, so it is a Text panel for pickers, topic line, jump.
  const noDocFallback = isNonUsLabel && !docPending && !hasDoc;
  const showToggle = isNonUsLabel && hasDoc && hasText;
  const [format, setFormat] = useState<'pdf' | 'text'>('pdf'); // reset by the panel's remount key
  const textMode = sectionsBacked || noDocFallback || (showToggle && format === 'text');
  const formatTag: 'Text' | 'PDF' | null = showToggle
    ? null
    : isJp || sectionsBacked || noDocFallback ? 'Text'
    : source !== 'label' ? 'PDF'
    : isNonUsLabel && hasDoc && !hasText ? 'PDF'
    : null; // non-US label whose document is still loading (no flicker)
  const switchFormat = (next: 'pdf' | 'text') => {
    if (next === format) return;
    setFormat(next);
    onClearSections?.(); // PDF TOC numbers and structured numbers are different lists
  };
  const bodyRef = useRef<HTMLDivElement>(null);
  // Resolve the active concept to THIS column's region (US drugs may report no
  // region → treat as US). A concept can map to several sections in a region,
  // to a nearest-content fallback plus a note, or to nothing at all.
  // Resolve EVERY selected universal section to THIS column's region and UNION
  // the section families. `conceptSection` (the PDF scroll primary) is the first
  // pick's primary; `isUniversalConcept` is true if any pick is a real universal
  // section (vs a Cogni one-region pseudo-concept).
  const activeConcepts = concepts ?? [];
  const conceptKey = activeConcepts.map((c) => c.key).join('|');
  const { conceptNums, conceptSection, isUniversalConcept } = useMemo(() => {
    const r = resolveColumnConcepts(activeConcepts, region);
    return { conceptNums: r.nums, conceptSection: r.scroll, isUniversalConcept: r.isUniversal };
    // activeConcepts is captured via conceptKey (its stable key list).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptKey, region]);

  // Per-panel JP language toggle (independent per column). English by default.
  const [jpLang, setJpLang] = useState<JPLang>('en');
  // JP section options for the per-panel dropdown — fetched from the drug's OWN
  // JP sections (its main `sections` are empty).  Shares JPCompareColumn's React
  // Query cache key, so this does NOT add a second network request.  Only runs
  // for JP panels.
  const { data: jpData } = useQuery({
    queryKey: ['jp-sections', drugId],
    queryFn: () => authApi.getDrugJPSections(drugId) as Promise<{
      sections: JPSection[];
      template?: JPTemplateEntry[];
    }>,
    enabled: isJp && !!drugId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const jpSectionOpts = useMemo(
    () => jpSectionOptions(jpData?.template, jpData?.sections),
    [jpData],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevSection = useRef<string>(sectionNumber);
  const prevDrug = useRef<number>(drugId);
  // Track which (drug+section) we've already auto-scrolled, so a later
  // re-render/refetch never re-grabs the scroll and yanks the user back to the
  // match — when viewing "All sections" they must stay free to scroll above it.
  const scrolledKey = useRef<string>('');
  // Effective per-column selection: manual > universal > all (see
  // narrowedSections). A JP panel narrows on a UNIVERSAL pick like every other
  // panel, but still shows the whole bilingual label for the Cogni evidence
  // path, which points at a cited sentence and only scrolls.
  //
  // JP's sections are NOT on ``drug`` — its main ``sections`` are empty and the
  // real list comes from this panel's own /jp-sections fetch — so the family
  // has to expand against that list or nothing would ever match.
  const effSelected = useMemo<string[] | undefined>(() => (
    narrowedSections({
      manual: selectedSections,
      conceptSections: conceptNums ?? null,
      isUniversal: isUniversalConcept,
      isJp,
      present: sectionNumbersOf(isJp ? jpSectionOpts : (drug?.sections ?? [])),
    })
  ), [selectedSections, conceptNums, isUniversalConcept, isJp, jpSectionOpts, drug]);

  // ── PDF columns: the document's own TOC (lifted from the embed) drives both a
  // per-column section picker AND the "show only this section's pages" filter.
  // The drug's `sections` are wrong/empty for a non-US PDF, so the real sections
  // can only come from the viewer's TOC. ──────────────────────────────────────
  // Tagged with the document it came from: a column whose document changes (a
  // new search can match another one) must not judge the new document by the
  // old TOC — under a Universal Section pick that would hide the new viewer
  // before it ever reports its own TOC.
  const tocKey = `${source}|${primary?.document_id ?? ''}`;
  const [tocState, setTocState] = useState<{ key: string; entries: TOCEntry[] } | null>(null);
  const setPdfToc = useCallback((entries: TOCEntry[]) => setTocState({ key: tocKey, entries }), [tocKey]);
  const pdfTocLoaded = tocState?.key === tocKey;
  const pdfToc = useMemo(() => (pdfTocLoaded ? tocState!.entries : []), [pdfTocLoaded, tocState]);
  const pdfTocSections = useMemo<SectionResponse[]>(() => pdfToc.map((e, i) => ({
    id: e.id ?? i + 1,
    loinc_code: null,
    section_type: e.section_type ?? null,
    section_number: e.section_number,
    title: e.title ?? '',
    // Truthy content so SectionTreeSelect's hasContent keeps the row (TOC rows
    // are all real sections; the body is never displayed here).
    content_text: e.title || e.section_number || ' ',
    content_xml: null,
  })), [pdfToc]);
  const pdfTocNums = useMemo(
    () => pdfToc.map((e) => e.section_number).filter((s): s is string => !!s),
    [pdfToc],
  );
  // Which PDF sections to show: manual pick wins; else the universal pick,
  // family-expanded against THIS doc's real TOC numbers; else undefined (all).
  // Mirrors narrowedSections' precedence but against the PDF's own sections.
  const pdfEff = useMemo<string[] | undefined>(() => {
    if (selectedSections) return selectedSections;
    if (!conceptNums || conceptNums.length === 0) return undefined;
    const fam = expandSectionFamilies(conceptNums, pdfTocNums);
    return fam.length ? fam : undefined; // not in this doc → show all pages
  }, [selectedSections, conceptNums, pdfTocNums]);

  // A Universal Section pick (no manual pick) that this label doesn't have
  // shows NOTHING and ticks nothing — same rule as its export — instead of
  // falling back to the whole label. Known only once the label's own section
  // list has loaded (a PDF's TOC arrives from its viewer; one with no section
  // numbers has none of them, as in the export).
  const universalPresent: string[] | null =
    !isUniversalConcept || selectedSections !== undefined ? null
      : textMode ? (drug ? sectionNumbersOf(drug.sections) : null)
      : isJp ? (jpData ? sectionNumbersOf(jpSectionOpts) : null)
      : pdfTocLoaded ? pdfTocNums : null;
  const universalMissing = universalPresent !== null
    && expandSectionFamilies(conceptNums ?? [], universalPresent).length === 0;
  const shownSelected = universalMissing ? [] : effSelected;
  const shownPdf = universalMissing ? [] : pdfEff;
  const missingLabels = activeConcepts.filter((c) => c.universal_order != null).map((c) => c.label).join(' / ');

  // All content-bearing sections (sorted), then narrowed to effSelected.
  const secs = useMemo(() => {
    const all = sectionsFor(drug, '');
    const eff = effectiveSelection(drug?.sections, shownSelected);
    return all.filter((s) => eff.has(s.section_number || `__${s.title ?? ''}`));
  }, [drug, shownSelected]);

  // PDF columns: drive the embedded viewer to the concept's section.
  const viewerRef = useRef<ViewerHandle>(null);
  useEffect(() => {
    if (!conceptSection || textMode) return;
    let tries = 0;
    const iv = setInterval(() => {
      // Universal-section picks resolve to a region's PRIMARY number, which may be
      // a parent absent from a subsection-only PDF TOC (EU 4.4 → 4.4.1/4.4.2). Opt
      // into the viewer's parent→first-subsection fallback so the PDF panel scrolls
      // to the same family the text panel narrows to. (Exact match still wins.)
      // `deferUntilLoaded`: a viewer still loading its TOC queues the jump
      // instead of reporting a false "not in this document's contents" miss
      // (and this poll no longer gives up on a PDF slower than 5 s).
      const ok = viewerRef.current?.scrollToSection?.(conceptSection, {
        allowSubsectionPrefix: true,
        deferUntilLoaded: true,
      });
      if (ok || ++tries > 25) clearInterval(iv);
    }, 200);
    return () => clearInterval(iv);
  }, [conceptSection, textMode, primary?.document_id]);

  useEffect(() => {
    const sectionChanged = prevSection.current !== sectionNumber;
    const drugChanged = prevDrug.current !== drugId;
    prevSection.current = sectionNumber;
    prevDrug.current = drugId;
    if (!textMode) return;
    const cont = scrollRef.current;
    if (sectionChanged && !drugChanged && cont) cont.scrollTop = 0;
    if (!phrase && !evidenceChunkText && !scrollToSectionNumber) return;
    // Jump to the match ONCE per (drug, section); afterwards scrolling is the
    // user's. Cogni → the evidence-chunk-highlight block; keyword → first
    // <mark>; if neither materialises, fall back to the evidence section.
    const key = `${drugId}|${sectionNumber}`;
    if (scrolledKey.current === key) return;
    const scrollTo = (el: Element, c: HTMLElement) => {
      c.scrollTop += el.getBoundingClientRect().top - c.getBoundingClientRect().top - 48;
      scrolledKey.current = key;
    };
    let tries = 0;
    const iv = setInterval(() => {
      const c = scrollRef.current;
      if (!c) { if (++tries > 30) clearInterval(iv); return; }
      const hit = c.querySelector('.evidence-chunk-highlight') ?? c.querySelector('mark');
      if (hit) { scrollTo(hit, c); clearInterval(iv); return; }
      // Give the highlight ~0.8 s to apply; if it never matches, scroll to the
      // evidence section heading so the panel still lands on the right place.
      if (tries >= 8 && scrollToSectionNumber) {
        const sec = c.querySelector(`[data-section="${scrollToSectionNumber}"]`);
        if (sec) { scrollTo(sec, c); clearInterval(iv); return; }
      }
      if (++tries > 30) clearInterval(iv);
    }, 100);
    return () => clearInterval(iv);
  }, [phrase, evidenceChunkText, scrollToSectionNumber, sectionNumber, drugId, secs, textMode]);

  // US text panel: a Universal Section pick narrows the panel to that
  // section's family, so the section starts at the panel's top. Without this
  // the panel kept its old scrollTop — clamped into the shorter content — and
  // landed partway down the section.
  const universalKey = isUniversalConcept ? conceptKey : null;
  useEffect(() => {
    if (!textMode || !universalKey) return;
    const cont = scrollRef.current;
    if (cont) cont.scrollTop = 0;
  }, [universalKey, textMode]);

  const colDrop =
    onReorderDrop || onDropDrug
      ? {
          onDragOver: (e: DragEvent) => {
            if (e.dataTransfer.types.includes('text/plain')) e.preventDefault();
          },
          onDrop: (e: DragEvent) => {
            const raw = e.dataTransfer.getData('text/plain');
            if (raw.startsWith('col:')) {
              if (onReorderDrop) {
                e.preventDefault();
                const fromId = Number(raw.slice(4));
                if (fromId && fromId !== drugId) onReorderDrop(fromId, drugId);
              }
            } else if (/^\d+$/.test(raw) && onDropDrug) {
              e.preventDefault();
              onDropDrug(Number(raw));
            }
          },
        }
      : {};

  // The section-text panel (US `label` drugs; also the fallback for a non-US
  // `label` drug whose regional PDF can't be resolved). Extracted to a const so
  // both the sectionsBacked branch and RegionalPdfColumn's fallback reuse it.
  const sectionTextPanel = (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 text-sm leading-relaxed text-ink-2">
      {secs.length === 0 ? (
        <div className="text-ink-3">No content for this section.</div>
      ) : (
        secs.map((s) => (
          <div key={s.id} data-section={s.section_number ?? ''} className="mb-6 border-t border-line-subtle pt-4 first:border-t-0 first:pt-0">
            {terms.length ? (
              <h2
                className="mb-2 text-sm font-bold uppercase tracking-wide text-ink"
                dangerouslySetInnerHTML={{ __html: highlightLabel(sectionLabel(s.section_number, s.title), terms, colors, englishExact) }}
              />
            ) : (
              <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-ink">
                {sectionLabel(s.section_number, s.title)}
              </h2>
            )}
            <SplContentRenderer
              drugName={drug?.brand_name || ''}
              contentXml={s.content_xml}
              contentText={s.content_text}
              englishExact={!!englishExact}
              {...(terms.length ? { highlightTerms: terms, highlightColors: colors } : {})}
              {...(evidenceChunkText ? { evidenceChunkText } : {})}
            />
          </div>
        ))
      )}
    </div>
  );

  // Where each ?usec= pick landed in THIS panel (R8): structured sections in a
  // Text panel (incl. the no-document fallback), JP options in JP, else the PDF TOC.
  const topicSections: { section_number: string | null; title: string | null }[] =
    textMode ? (drug?.sections ?? []) : isJp ? jpSectionOpts : pdfTocSections;
  const topic = drug && universalPicks?.length ? topicEntries(universalPicks, region, topicSections) : [];
  const jump = (n: string) => {
    if (textMode || isJp) {
      if (bodyRef.current) findSectionBlock(bodyRef.current, n)?.scrollIntoView({ block: 'start' });
    } else {
      viewerRef.current?.scrollToSection?.(n, { allowSubsectionPrefix: true, deferUntilLoaded: true });
    }
  };
  const showTextPicker = textMode && !!drug?.sections && drug.sections.length > 0;
  const showJpTools = isJp;
  const showPdfPicker = !textMode && !isJp && source !== 'fda_letter' && pdfTocSections.length > 0;

  return (
    <div
      {...colDrop}
      className="flex h-full w-full flex-col overflow-hidden rounded border border-line bg-white"
      style={accentColor ? { borderTop: `3px solid ${accentColor}` } : undefined}
      {...{ [USER_HIGHLIGHT_DRUG_ATTR]: drugId }}
    >
      <header
        {...{ [USER_HIGHLIGHT_IGNORE_ATTR]: '' }}
        className="sticky top-0 z-10 flex flex-col gap-1.5 border-b border-line bg-panel px-3 py-2"
        draggable={!!onReorderDrop}
        onDragStart={
          onReorderDrop
            ? (e) => {
                e.dataTransfer.setData('text/plain', `col:${drugId}`);
                e.dataTransfer.effectAllowed = 'move';
              }
            : undefined
        }
        title={onReorderDrop ? 'Drag to reorder' : undefined}
      >
        <div className="flex items-center gap-2">
          {onReorderDrop && <span className="cursor-grab text-line-input active:cursor-grabbing">⠿</span>}
          {slotLetter && accentColor && (
            <span
              data-testid="slot-badge"
              className="shrink-0 rounded-ctl px-1.5 font-mono text-[11px] text-white"
              style={{ backgroundColor: accentColor }}
            >
              {slotLetter}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <button
              onClick={() => onOpen(drugId)}
              title="Open full label with the keyword highlighted"
              className="block max-w-full truncate text-left font-semibold text-ink hover:text-accent-deep hover:underline"
            >
              {drug?.brand_name || `Drug ${drugId}`}
            </button>
            <div data-testid="panel-subtitle" className="truncate text-xs text-ink-3">
              {drug?.generic_name}
              {drug?.manufacturer ? ` · ${drug.manufacturer}` : ''}
            </div>
          </div>
          {onRemove && (
            <button onClick={() => onRemove(drugId)} title="Remove" className="rounded px-1 text-ink-3 hover:bg-line hover:text-ink-2">
              ✕
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span data-testid="region-chip" className="rounded-ctl border border-line-input bg-white px-1.5 font-mono text-[11px] text-ink-2">
            {regionDisplayLabel(region, usFdaVisible)}
          </span>
          {drug && (
            <span data-testid="version-line" className="text-ink-3">
              {panelVersionLine(region, drug.version_number, drug.effective_time, formatTag)}
            </span>
          )}
          {showToggle && (
            <div className="ml-auto inline-flex rounded-ctl border border-line-input bg-white p-0.5 text-[11px]">
              {(['pdf', 'text'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={format === f}
                  onClick={() => switchFormat(f)}
                  className={
                    'rounded-ctl px-2 py-0.5 transition-colors ' +
                    (format === f ? 'bg-accent text-white' : 'text-ink-2 hover:text-ink')
                  }
                >
                  {f === 'pdf' ? 'PDF' : 'Text'}
                </button>
              ))}
            </div>
          )}
        </div>

        {(showTextPicker || showJpTools || showPdfPicker) && (
          <div className="flex flex-wrap items-center gap-2">
            {showTextPicker && (
              <SectionTreeSelect
                sections={drug!.sections}
                selected={shownSelected}
                onChange={onSectionsChange}
              />
            )}
            {/* JP panel: per-panel EN/日本語 toggle + a section dropdown bound to the
                drug's OWN JP sections.  JP-only — never shown for US/EU/other. */}
            {showJpTools && (
              <>
                <div className="inline-flex rounded-ctl border border-line-input bg-white p-0.5 text-[11px]">
                  {(['en', 'ja'] as JPLang[]).map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setJpLang(l)}
                      className={
                        'rounded-ctl px-2 py-0.5 transition-colors ' +
                        (jpLang === l ? 'bg-accent font-medium text-white' : 'text-ink-3 hover:text-ink-2')
                      }
                      aria-pressed={jpLang === l}
                    >
                      {l === 'en' ? 'EN' : '日本語'}
                    </button>
                  ))}
                </div>
                {jpSectionOpts.length > 0 && (
                  <SectionTreeSelect
                    sections={jpSectionOpts}
                    selected={shownSelected}
                    onChange={onSectionsChange}
                  />
                )}
              </>
            )}
            {/* PDF columns (non-US label, or a regulatory-source match): a section
                picker built from the document's OWN TOC (lifted via onTocLoaded).
                Picking sections shows only their pages; clearing shows all. */}
            {showPdfPicker && (
              <>
                <span className="text-ink-3">Contents:</span>
                <SectionTreeSelect
                  sections={pdfTocSections}
                  selected={shownPdf}
                  onChange={onSectionsChange}
                />
              </>
            )}
          </div>
        )}

        {topic.length > 0 && (
          <div
            data-testid="topic-line"
            className="truncate text-[11px] text-ink-2"
            title={topic.map((e) => `${e.label} → ${e.text}`).join('; ')}
          >
            {topic.map((e, i) => (
              <span key={`${e.label}-${i}`} data-testid="topic-entry">
                {i > 0 ? '; ' : ''}
                {`${e.label} → ${e.text}`}
                {e.jumpTo != null && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      onClick={() => jump(e.jumpTo!)}
                      className="text-accent hover:underline"
                    >
                      jump
                    </button>
                  </>
                )}
              </span>
            ))}
          </div>
        )}
      </header>

      <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center text-ink-3">Loading…</div>
      ) : universalMissing ? (
        <div data-testid="universal-missing" className="p-4 text-sm text-ink-3">
          {`This label has no “${missingLabels}” section.`}
        </div>
      ) : textMode ? (
        sectionTextPanel
      ) : isJp ? (
        // JP (region='JP'): bilingual PMDA package-insert accordion, fed by this
        // panel's own /drugs/{id}/jp-sections fetch, with its own EN/日本語 toggle
        // (wired to the header buttons above).  English by default.  Shows the
        // WHOLE label (effSelected is manual-only for JP) and scrolls to / boxes
        // the evidence: Cogni → blue evidence-chunk block (evidenceChunkText);
        // keyword → the colored search terms (<mark>). Discriminate on
        // evidenceChunkText so Cogni shows only the blue block, not a yellow box.
        <JPCompareColumn
          drugId={drugId}
          lang={jpLang}
          setLang={setJpLang}
          selectedSections={effSelected}
          highlightTerms={evidenceChunkText ? [] : terms}
          highlightColors={evidenceChunkText ? [] : colors}
          englishExact={!!englishExact}
          evidenceChunkText={evidenceChunkText ?? null}
          scrollToSectionNumber={conceptSection ?? scrollToSectionNumber ?? null}
        />
      ) : isFr ? (
        <div className="min-h-0 flex-1 overflow-auto">
          <FrCompareColumn drugId={drugId} fallback={sectionTextPanel} />
        </div>
      ) : isNonUsLabel ? (
        // Non-US `label` drug (no document_id) → resolve + render its regional
        // PDF viewer, falling back to the section-text panel while loading / when
        // no document exists so the panel is never empty.
        <div className="min-h-0 flex-1 overflow-hidden">
          <RegionalPdfColumn
            drugId={drugId}
            region={region}
            viewerRef={viewerRef}
            phrase={phrase}
            terms={terms}
            colors={colors}
            fallback={sectionTextPanel}
            englishExact={!!englishExact}
            evidenceText={evidenceChunkText}
            evidenceSection={scrollToSectionNumber}
            visibleSectionNumbers={pdfEff}
            onTocLoaded={setPdfToc}
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden">
          {source === 'fda_letter' ? (
            primary?.section_id && primary.section_id > 0 ? (
              // FdaLetterPageViewer carries NO internal scroll (the full
              // viewers sit inside a naturally-scrolling page). The compare
              // column constrains height, so wrap it in an overflow-y-auto
              // region to scroll through ALL the letter's pages — matching
              // the full /drug/{id} viewer. Regulatory viewers manage their
              // own scroll, so they keep the bare overflow-hidden wrapper.
              <div className="h-full overflow-y-auto px-3 py-2">
                <FdaLetterPageViewer
                  englishExact={!!englishExact}
                  section_id={primary.section_id}
                  activeChunkId={primary.chunk_id ?? null}
                  highlightTerms={terms.length ? terms : null}
                  highlightColors={colors.length ? colors : null}
                />
              </div>
            ) : (
              <div className="p-4 text-sm text-ink-3">No letter page reference for this match.</div>
            )
          ) : primary?.document_id ? (
            regulatoryViewer(source, primary.document_id, primary.chunk_id ? [primary.chunk_id] : [], phrase, terms, colors, viewerRef, pdfEff, setPdfToc, englishExact)
          ) : (
            <div className="p-4 text-sm text-ink-3">
              Open this {drug?.region || ''} drug from the rail (click the name) to load its document.
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

// ─── page-level metadata probe (rail filter + footer names) ───────────────────
/** Display metadata for a rail row whose own fields are incomplete. */
type RowMeta = {
  status: 'pending' | 'error' | 'ok';
  brand_name?: string | null;
  generic_name?: string | null;
  manufacturer?: string | null;
  region?: string | null;
};
/** Renders nothing: reports one drug's hydrated metadata to the page. Same React
 *  Query key as RailRow / DrugColumn (`['drug', id]`), so it adds no request. It
 *  lives outside the tab content, so it runs whichever tab is active and whatever
 *  the filter hides. */
function DrugMetaProbe({ id, onMeta }: { id: number; onMeta: (id: number, m: RowMeta) => void }) {
  const { data, isError } = useDrugQuery(String(id));
  useEffect(() => {
    onMeta(id, isError
      ? { status: 'error' }
      : data
        ? { status: 'ok', brand_name: data.brand_name, generic_name: data.generic_name, manufacturer: data.manufacturer, region: data.region }
        : { status: 'pending' });
  }, [id, data, isError, onMeta]);
  return null;
}

// ─── one left-rail row (shared by keyword + Cogni) ────────────────────────────
export function RailRow({ r, isCogni, starred, inCompare, atCompareCap, atStarCap, onStar, onToggleCompare, accentColor, slotLetter, selectable, selectChecked, onSelectToggle, selectDisabled, usFdaVisible = false }: {
  r: KeywordSearchResultItem;
  isCogni: boolean;
  /** ★ shortlist membership (independent of the compare panels). */
  starred: boolean;
  /** True when this drug occupies one of the (≤100) compare columns. */
  inCompare: boolean;
  /** True when 100 columns are filled and this drug isn't one of them. */
  atCompareCap: boolean;
  /** True when MAX_STARS drugs are starred and this drug isn't one of them. */
  atStarCap: boolean;
  onStar: (id: number) => void;
  /** Click the row → add/remove this drug from the compare panels. */
  onToggleCompare: (id: number) => void;
  /** This drug's panel identity color (when inCompare) — matches its panel. */
  accentColor?: string | undefined;
  /** This drug's slot label (A-E, then 6…; when inCompare). */
  slotLetter?: string | undefined;
  /** Multi-select mode is on — show the tick box that feeds the bulk ★ bar. */
  selectable?: boolean;
  /** This row is ticked for the bulk action (independent of ★ and of panels). */
  selectChecked?: boolean;
  /** Tick/untick this row. */
  onSelectToggle?: (id: number) => void;
  /** The selection is at its cap and this row is not in it — the box is locked
   *  rather than silently refusing the click. */
  selectDisabled?: boolean;
  /** US-Drugs@FDA live for this user: the slot badge reads the new region label. */
  usFdaVisible?: boolean;
}) {
  const { data: drug } = useDrugQuery(String(r.drug_id), { enabled: isCogni || !r.manufacturer || !r.brand_name });
  const manufacturer = r.manufacturer || drug?.manufacturer || null;
  const region = r.region || drug?.region || null;
  // "manufacturer · match count" (count only when the row has one; keyword only —
  // it is a real match count, off for Cogni).
  const subtitle = [manufacturer, !isCogni && r.total_matches ? r.total_matches : null].filter(Boolean).join(' · ');
  const displayName = r.brand_name || drug?.brand_name || `Drug ${r.drug_id}`;
  const compareDisabled = !inCompare && atCompareCap;
  const starDisabled = !starred && atStarCap;
  const badgeText = [slotLetter, region ? regionDisplayLabel(region, usFdaVisible) : region].filter(Boolean).join(' · ');
  return (
    <li
      data-testid={`rail-row-${r.drug_id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(r.drug_id));
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className={`rounded border px-2 py-1.5 text-sm ${
        inCompare ? 'border-line bg-panel' : starred ? 'border-line-subtle bg-panel' : 'border-transparent'
      }`}
    >
      <div className="flex items-center gap-1.5">
        {/* Multi-select tick box. Plain controlled checkbox:
            an onClick + preventDefault here (the old shift-range hook) left the
            tick one click behind, because React reverts the DOM node while its
            cached value has already flipped. */}
        {selectable && (
          <input
            type="checkbox"
            checked={!!selectChecked}
            disabled={!!selectDisabled}
            aria-label={`Select ${displayName}`}
            title={selectDisabled ? `Selection limit reached (${MAX_STARS}) — untick a row first` : undefined}
            onChange={() => onSelectToggle?.(r.drug_id)}
            className={`h-3.5 w-3.5 shrink-0 accent-[#C98A1B] ${
              selectDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
            }`}
          />
        )}
        <button
          onClick={() => onStar(r.drug_id)}
          disabled={starDisabled}
          title={
            starred ? 'Remove from starred shortlist'
              : starDisabled ? `Starred list is full (${MAX_STARS}) — remove a star first`
              : 'Star (shortlist — save, share)'
          }
          className={`text-base leading-none ${
            starred ? '' : starDisabled ? 'cursor-not-allowed text-line' : 'text-line-input hover:text-ink-3'
          }`}
          style={starred ? { color: STAR_GOLD } : undefined}
        >
          {starred ? '★' : '☆'}
        </button>
        <button
          onClick={() => onToggleCompare(r.drug_id)}
          disabled={compareDisabled}
          title={inCompare ? 'Remove from comparison' : compareDisabled ? `Compare is full (${MAX_PANELS}) — remove a column first` : 'Add to comparison'}
          className={`min-w-0 flex-1 truncate text-left font-medium ${
            inCompare ? 'text-ink' : compareDisabled ? 'cursor-not-allowed text-ink-3' : 'text-ink hover:text-accent-deep'
          }`}
        >
          {displayName}
        </button>
        {/* Slot badge (in a panel) or "+ Add" (not yet). */}
        {inCompare && badgeText && (
          <span
            className="shrink-0 rounded-ctl px-1 font-mono text-[10px] text-white"
            style={{ backgroundColor: accentColor }}
          >
            {badgeText}
          </span>
        )}
        {!inCompare && (
          <button
            onClick={() => onToggleCompare(r.drug_id)}
            disabled={compareDisabled}
            title={compareDisabled ? `Compare is full (${MAX_PANELS}) — remove a column first` : 'Add to comparison'}
            className="shrink-0 rounded-ctl border border-line px-1.5 text-[10px] font-medium text-ink-2 hover:bg-panel disabled:cursor-not-allowed disabled:text-ink-3"
          >
            + Add
          </button>
        )}
      </div>
      {subtitle && (
        <div className="truncate pl-6 text-[10px] text-ink-3" title={manufacturer ?? undefined}>
          {subtitle}
        </div>
      )}
    </li>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────
export default function CompareKeywordPage() {
  const usFdaVisible = isUsFdaVisible(useIsAdmin());
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // SearchPage writes the handoff under this tab's keys; read the same ones.
  const { storageKey, reportRun } = useTab();
  // Rail: Results | Starred tab, the client-side "Filter results" box (displayed
  // rows only, not persisted) and the hydrated metadata of rows whose own fields
  // are incomplete (DrugMetaProbe below) — the filter's data source.
  const [railTab, setRailTab] = useState<'results' | 'starred'>(() =>
    params.get('saved') === '1' ? 'starred' : 'results');
  const [railFilter, setRailFilter] = useState('');
  const [rowMeta, setRowMeta] = useState<Record<number, RowMeta>>({});
  const onMeta = useCallback((id: number, m: RowMeta) => {
    setRowMeta((prev) => {
      const old = prev[id];
      if (old && old.status === m.status && old.brand_name === m.brand_name && old.generic_name === m.generic_name
        && old.manufacturer === m.manufacturer && old.region === m.region) return prev;
      return { ...prev, [id]: m };
    });
  }, []);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Browser export progress on the shared 0-100 scale (utils/exportProgress);
  // null while idle. The controller aborts the running export (Cancel).
  const [exportPct, setExportPct] = useState<number | null>(null);
  // Page-layout generation (EU/AU/UK/CA/EPAR sections): sections done / waited on.
  const [layoutProgress, setLayoutProgress] = useState<{ done: number; total: number } | null>(null);
  const exportAbort = useRef<AbortController | null>(null);
  // The latest server-side export started from this page (big exports run on
  // the server — docs/export/SERVER_SIDE_EXPORT_PLAN.md). Polled until done.
  const [serverJob, setServerJob] = useState<{ id: string; status: ExportJobStatus; pct: number } | null>(null);
  const [exportMessage, setExportMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  // Manual highlights (page memory only): shown on screen + in Export, not Share.
  const userHighlights = useUserHighlights();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareWarning, setShareWarning] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Two modes share this page, and BOTH now use the identical rail model: the
  // rail is the search RESULTS (★ star-to-shortlist, drag into panels) and the
  // compare set is the ★ shortlist. KEYWORD (default): rail = keyword results.
  // COGNI (?src=cogni): rail = the AI search's drug_results, replayed from the
  // react-query cache via the stashed request; highlight = meaningful words
  // from the query.
  const isCogni = params.get('src') === 'cogni';

  // AI search results handed off directly by SearchPage
  // (sessionStorage('cogni-results') = { query, drugs: DrugResultV2[] }). We do
  // NOT refetch: page-1 of the AI search is streamed, so there's no query cache
  // to replay — the search page already holds the exact results the user saw.
  const cogniData = useMemo<{ query: string; drugs: DrugResultV2[]; filters?: Record<string, unknown>; total?: number } | null>(() => {
    if (!isCogni) return null;
    try {
      const saved = sessionStorage.getItem(storageKey('cogni-results'));
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, [isCogni, storageKey]);
  const cogniQuery = cogniData?.query ?? '';
  // COGNI infinite scroll: page 1 is the handed-off set (streamed on the search
  // page, no cache to replay); pages 2+ are fetched HERE via the react-query
  // paginated hook as the rail scrolls, accumulated + deduped, up to `total`.
  const [cogniPage, setCogniPage] = useState(1);
  const [extraCogni, setExtraCogni] = useState<DrugResultV2[]>([]);
  const cogniMore = useSearchQueryV2Drugs(
    cogniQuery,
    { ...(cogniData?.filters ?? {}), page: cogniPage } as Parameters<typeof useSearchQueryV2Drugs>[1],
    isCogni && cogniPage > 1,
  );
  useEffect(() => {
    const more = cogniMore.data?.drug_results;
    if (!more?.length) return;
    setExtraCogni((prev) => {
      const seen = new Set([
        ...(cogniData?.drugs ?? []).map((d) => d.drug_id),
        ...prev.map((d) => d.drug_id),
      ]);
      const fresh = more.filter((d) => !seen.has(d.drug_id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [cogniMore.data, cogniData]);
  const cogniAllDrugs = useMemo(
    () => [...(cogniData?.drugs ?? []), ...extraCogni],
    [cogniData, extraCogni],
  );
  const cogniTotal = cogniData?.total ?? cogniAllDrugs.length;
  const cogniHasMore = isCogni && cogniAllDrugs.length < cogniTotal;
  const cogniLoadingMore = isCogni && (cogniMore.isLoading || cogniMore.isFetching);

  // Raw parsed ?stars= (uncapped): the legacy-link migration dedupes THEN caps.
  const rawStars: number[] = useMemo(
    () =>
      (params.get('stars') ?? '')
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0),
    [params],
  );
  const stars: number[] = useMemo(
    () => rawStars.slice(0, MAX_STARS),
    [rawStars],
  );
  // Legacy `?view=stars` links (the removed N-column view): the stars become the
  // columns on the FIRST render, then the mount effect below rewrites the URL.
  const oldStarsLink = params.get('view') === 'stars' && stars.length > 0;
  const hasViewParam = params.has('view');
  // The compare-COLUMN set — INDEPENDENT of ★ stars. These are the (≤100) drugs
  // shown side-by-side. A drug can be in columns, starred, both, or neither.
  // ?panels= in the URL; seeded to the first 2 results on load.
  const panels: number[] = useMemo(() => {
    const src = oldStarsLink
      ? rawStars
      : (params.get('panels') ?? '')
          .split(',')
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isFinite(n) && n > 0);
    return Array.from(new Set(src)).slice(0, MAX_PANELS);
  }, [params, oldStarsLink, rawStars]);
  // One-time migration of a legacy `?view=` link: stars (URL order) REPLACE the
  // panels, `view` and `cols` go, everything else (saved, listId, usec, kw) stays.
  // A saved list with more than 100 raw ids says so.
  useEffect(() => {
    if (params.has('view')) {
      const next = new URLSearchParams(params);
      if (oldStarsLink) {
        next.set('panels', panels.join(','));
        next.delete('cols');
      }
      next.delete('view');
      setParams(next, { replace: true });
    }
    if (params.get('saved') === '1') {
      const rawCount = new Set(rawStars).size;
      if (rawCount > MAX_PANELS) {
        setExportMessage({ kind: 'info', text: `Showing the first ${MAX_PANELS} of ${rawCount} starred.` });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const kwParam = params.get('kw') ?? '';

  const kwRequest = useMemo<KeywordSearchRequest | null>(() => {
    try {
      const saved = sessionStorage.getItem(storageKey('kw-request'));
      return saved ? (JSON.parse(saved) as KeywordSearchRequest) : null;
    } catch {
      return null;
    }
  }, [storageKey]);
  // Rows the results table ticked (kw-compare-seed), scoped to the executed
  // search: a ticked drug outside the rail's loaded window keeps its highlights
  // and source document. A seed from another search is ignored.
  const seedRows = useMemo<KeywordSearchResultItem[]>(() => {
    if (!kwRequest) return [];
    try {
      const raw = sessionStorage.getItem(storageKey('kw-compare-seed'));
      const seed = raw ? (JSON.parse(raw) as { search?: string; rows?: KeywordSearchResultItem[] }) : null;
      return seed && seed.search === kwSearchIdentity(kwRequest) && Array.isArray(seed.rows) ? seed.rows : [];
    } catch {
      return [];
    }
  }, [storageKey, kwRequest]);
  // Refine chips carried over from the keyword results table (?refine=). The
  // rail and the default A/B pair show only the drugs that pass them — the
  // same set the user narrowed the results to. Keyed on the raw param, not
  // `params`, which is a new object on every URL write (panels, stars, cols).
  const refineParam = !isCogni ? params.get('refine') : null;
  const refineChips = useMemo(() => parseRefineParam(refineParam), [refineParam]);
  // A filter can leave the first pages with few or no matches; bigger pages
  // keep the request count down while the rail fills.
  const pageSize = refineChips.length > 0 ? FILTERED_RAIL_PAGE_SIZE : 10;
  // KEYWORD rail. Disabled in cogni mode (null request → hook short-circuits).
  const queryClient = useQueryClient();
  // Near on-demand (spec §5.8): the rail pages with the URL sort and gets its
  // passages merged in by the hook, so it shows the same order as the table.
  const sortParam = params.get(KW_SORT_PARAM);
  const railSort = useMemo(() => (isCogni ? null : parseSortParam(sortParam)), [isCogni, sortParam]);
  const rail = useKeywordSearchInfinite(
    isCogni ? null : kwRequest,
    pageSize,
    isCogni ? undefined : { passages: 'on_demand', sort: railSort },
  );
  const railDrugPages = (rail.data?.pages ?? []).filter(
    (p): p is KeywordSearchResponse => !!p && p.kind === 'drugs',
  );
  const railOnDemand = railDrugPages.some((p) => p.passages_on_demand === true);
  const railModesDisagree = railOnDemand && railDrugPages.some((p) => p.passages_on_demand !== true);
  const jpJaCorpus = !isCogni && kwRequest?.sources?.includes('jp_ja') ? 'jp_ja' : null;
  const keywordResults: KeywordSearchResultItem[] = useMemo(() => {
    // Never show rows from both modes while a flip is being reset.
    if (railModesDisagree) return [];
    const seen = new Set<number>();
    const out: KeywordSearchResultItem[] = [];
    for (const p of rail.data?.pages ?? []) {
      if (p && p.kind === 'drugs') {
        for (const r of p.results) {
          if (!seen.has(r.drug_id)) {
            seen.add(r.drug_id);
            out.push(r);
          }
        }
      }
    }
    return out;
  }, [rail.data, railModesDisagree]);

  // COGNI rail — map each handed-off DrugResultV2 into the rail's
  // KeywordSearchResultItem shape. The AI result has no manufacturer, so the
  // rail row (RailRow) fetches it per drug via useDrugQuery in cogni mode; and
  // there's no match count (not a keyword search), so total_matches stays 0 and
  // the number badge is hidden.
  const cogniResults: KeywordSearchResultItem[] = useMemo(() => {
    return cogniAllDrugs.map((r) => ({
      drug_id: r.drug_id,
      brand_name: r.drug_name,
      generic_name: r.generic_name ?? null,
      manufacturer: null,
      pharmacological_class: null,
      region: r.region ?? 'US',
      set_id: null,
      application_number: null,
      total_matches: 0,
      best_rank: r.score ?? 0,
      highlights: [],
    }));
  }, [cogniAllDrugs]);

  const results: KeywordSearchResultItem[] = isCogni ? cogniResults : keywordResults;
  // By-id lookup for panels: the rail's rows plus seeded rows it has not loaded.
  // The rail itself keeps using `results`.
  const resultLookup = useMemo(
    () => [...results, ...seedRows.filter((s) => !results.some((r) => r.drug_id === s.drug_id))],
    [results, seedRows],
  );

  // COGNI: the results table's filters (Region / year / last-updated / refine),
  // carried over on this URL by SearchPage's hand-off (2.4). Keyed on the
  // present filter params only, not `params` (new on every URL write).
  const cogniFilterKey = isCogni
    ? COGNI_TABLE_FILTER_PARAMS
      .filter((k) => params.get(k))
      .map((k) => `${k}=${encodeURIComponent(params.get(k)!)}`)
      .join('&')
    : '';
  // Approval dates are reliable for every region (2026-10-15 backfill).
  const cogniShowApprovalDate = true;
  const cogniTableFilters = useMemo(
    () => (cogniFilterKey ? parseCogniTableFilters(new URLSearchParams(cogniFilterKey)) : null),
    [cogniFilterKey],
  );
  // What the rail is narrowed by, as labels for its "Filtered:" note.
  const railFilterLabels = useMemo(
    () => (cogniTableFilters
      ? cogniTableFilterLabels(cogniTableFilters, cogniShowApprovalDate, usFdaVisible)
      : refineChips.map((c) => `${REFINE_KIND_LABELS[c.kind]}: ${formatRefineChipValue(c, usFdaVisible)}`)),
    [cogniTableFilters, cogniShowApprovalDate, refineChips, usFdaVisible],
  );
  const railFilterActive = railFilterLabels.length > 0;

  // `results` stays unfiltered: it is also the by-id lookup for panels/stars,
  // which may hold any drug.
  const filteredResults = useMemo(() => {
    if (cogniTableFilters && railFilterActive) {
      const pass = new Set(
        cogniAllDrugs
          .filter((d) => matchesCogniTableFilters(d, cogniTableFilters, cogniShowApprovalDate))
          .map((d) => d.drug_id),
      );
      return results.filter((r) => pass.has(r.drug_id));
    }
    return filterByRefineChips(results, refineChips, true);
  }, [results, refineChips, kwRequest, cogniTableFilters, railFilterActive, cogniAllDrugs, cogniShowApprovalDate]);

  // Register the keyword rail with the shell so CEPI can offer it ("Answer
  // from these N results"). The rows are the rail's Results with refine chips
  // applied (not the transient "Filter results" box or the Starred tab),
  // passages already merged by the rail hook, cut to what capture keeps. Loading more rows is a new run. Cogni hands off from its own tab,
  // so it is not reported here.
  const lastRailReportRef = useRef<string | null>(null);
  useEffect(() => {
    if (isCogni || !kwRequest || rail.isFetching || railModesDisagree) return;
    const first = railDrugPages[0];
    if (!first) return;
    const rows = filteredResults.slice(0, CAPTURE_MAX_ROWS);
    const sig = JSON.stringify([kwSearchIdentity(kwRequest), refineParam, rows.map((r) => r.drug_id)]);
    if (lastRailReportRef.current === sig) return;
    lastRailReportRef.current = sig;
    reportRun({
      kind: 'set',
      capability: 'keyword',
      count: rows.length,
      queryText: (kwRequest.groups ? keywordSearchTerms(kwRequest.groups) : kwRequest.rows?.map((r) => r.keyword) ?? [])
        .filter(Boolean).join(' '),
      // The stored request keeps the results page's offset; the rail always
      // starts at row 1.
      request: { ...kwRequest, offset: 0 },
      response: { ...first, results: rows, passages_on_demand: false },
    });
  }, [isCogni, kwRequest, rail.isFetching, railModesDisagree, railDrugPages, refineParam, filteredResults, reportRun]);

  // Rail infinite-scroll: keyword pages the kw search; cogni pages the AI search
  // (page 2+) and accumulates into extraCogni.
  const railRef = useRef<HTMLDivElement>(null);
  // The Starred tab and the "Filter results" box change DISPLAYED rows only: they
  // never page the search (explicit "Load more results" still does).
  const activeTab: 'results' | 'starred' = railTab;
  const railPagingOn = activeTab === 'results' && !railFilter.trim();
  const hasMoreResults = isCogni ? cogniHasMore : !!rail.hasNextPage;
  const onRailScroll = (e: UIEvent<HTMLElement>) => {
    if (!railPagingOn) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight >= 240) return;
    if (isCogni) {
      if (cogniHasMore && !cogniLoadingMore) setCogniPage((p) => p + 1);
    } else if (rail.hasNextPage && !rail.isFetchingNextPage) {
      rail.fetchNextPage();
    }
  };
  // With a filter that matches little, "fill the rail" would page through the
  // WHOLE result set. Auto-fill stops after the first FILTERED_RAIL_AUTO_ROWS
  // results (the results page's own page size); "Load more results" goes on.
  const railAutoFillCapped = isCogni
    ? cogniAllDrugs.length >= FILTERED_RAIL_AUTO_ROWS
    : refineChips.length > 0 && (rail.data?.pages.length ?? 0) * pageSize >= FILTERED_RAIL_AUTO_ROWS;
  // COGNI pages 2+ only on scroll — except while a carried-over filter leaves
  // the rail short of filling: then pull pages (up to the same cap) so the
  // matches on later pages show up without the user scrolling an empty rail.
  useEffect(() => {
    if (!railPagingOn || !isCogni || !railFilterActive || railAutoFillCapped) return;
    const el = railRef.current;
    if (el && cogniHasMore && !cogniLoadingMore && el.scrollHeight <= el.clientHeight + 4) {
      setCogniPage((p) => p + 1);
    }
  }, [railPagingOn, isCogni, railFilterActive, railAutoFillCapped, cogniHasMore, cogniLoadingMore, cogniAllDrugs.length]);
  useEffect(() => {
    if (!railPagingOn || isCogni || railAutoFillCapped || rail.isFetchNextPageError) return;
    const el = railRef.current;
    if (el && rail.hasNextPage && !rail.isFetchingNextPage && el.scrollHeight <= el.clientHeight + 4) {
      rail.fetchNextPage();
    }
  }, [railPagingOn, isCogni, railAutoFillCapped, rail.isFetchNextPageError, rail.hasNextPage, rail.isFetchingNextPage, rail.data]);

  // First TEXT row: field rows (Manufacturer, Brand, ...) are not label text.
  const firstRow =
    kwRequest?.rows?.find((r) => !r.drug_field) ??
    (kwRequest as unknown as { groups?: { rows?: { keyword?: string; section_scope?: string; drug_field?: string | null }[] }[] })
      ?.groups?.flatMap((g) => g.rows ?? []).find((r) => !r.drug_field);
  // The "phrase" shown/highlighted: keyword phrase in keyword mode, raw query in
  // cogni mode (the on-screen highlight uses `terms` below).
  const phrase = isCogni ? cogniQuery : kwParam || firstRow?.keyword || '';
  const searchedSection = isCogni ? '' : (firstRow?.section_scope ?? '').split('.')[0] ?? '';

  // Highlight terms + colors. KEYWORD: per-row colors (AND/OR rows). COGNI: the
  // meaningful words from ?q in a single color scheme.
  const coloredTerms = useMemo(() => {
    // COGNI: semantic search — highlight the per-drug AI EVIDENCE chunk (see
    // cogniEvidence / panelHL below), NOT the query words. So no query-word
    // colors + no word legend here.
    if (isCogni) return [];
    if (isCogni) return buildColoredTerms(cogniTerms(cogniQuery));
    const allRows: { keyword?: string; match_type?: string; drug_field?: string | null }[] =
      kwRequest?.rows ??
      (kwRequest as unknown as { groups?: { rows?: { keyword?: string; match_type?: string; drug_field?: string | null }[] }[] })?.groups?.flatMap(
        (g) => g.rows ?? [],
      ) ??
      [];
    const rows = allRows.filter((r) => !r.drug_field);
    // Near (stemmed) → highlight each WORD individually: Near matches words that
    // can be far apart / in different sections, so the literal phrase often isn't
    // present in the panel to highlight or scroll to. Exact → keep the phrase.
    const rowKeywords = [
      ...rows.flatMap((r) => {
        const kw = (r?.keyword ?? '').trim();
        if (!kw) return [];
        if (r?.match_type === 'exact') return [kw];
        return kw.split(/\s+/).filter(Boolean);
      }),
      // Proximity operands are single words; a proximity-only search has no rows.
      ...proximityOperands(kwRequest?.groups ?? []),
    ];
    return buildColoredTerms(rowKeywords.length ? rowKeywords : phrase ? [phrase] : []);
  }, [isCogni, cogniQuery, kwRequest, phrase]);
  const highlightTerms = useMemo(() => coloredTerms.map((c) => c.term), [coloredTerms]);
  const highlightColors = useMemo(() => coloredTerms.map((c) => c.color), [coloredTerms]);

  // COGNI: per-drug AI evidence passage to highlight + scroll to in its panel.
  // Prefer the full chunk text, then the snippet, then the LLM excerpt.
  const cogniEvidence = useMemo(() => {
    const m = new Map<number, { section: string | null; chunkText: string; snippet: string; region: string }>();
    if (!isCogni) return m;
    for (const d of cogniAllDrugs) {
      // US (section text): pass the FULL chunk to SplContentRenderer's
      // evidenceChunkText — it anchor-matches start+end (robust, like the
      // drug-detail view). Non-US (PDF viewer): a short snippet is used to box
      // the text in the PDF text layer (a full chunk won't match there).
      const chunkText = d.evidence?.chunk_text || d.evidence?.snippet || d.evidence_text || '';
      const clean = chunkText.replace(/\s+/g, ' ').replace(/[.\s]+$/, '').trim();
      const cut = clean.search(/\.\s/);
      const snippet = (cut > 30 ? clean.slice(0, cut + 1) : clean.slice(0, 120)).trim();
      const section = d.evidence?.section_number || d.all_evidence_sections?.[0] || null;
      const region = d.region || 'US';
      if (chunkText || section) m.set(d.drug_id, { section, chunkText, snippet, region });
    }
    return m;
  }, [isCogni, cogniAllDrugs]);
  // Per-panel highlight source: keyword = shared query terms. Cogni US = none
  // (the evidence chunk is the block evidence-chunk-highlight). Cogni non-US =
  // the snippet, boxed in the PDF text layer via terms.
  const panelHL = (id: number) => {
    if (!isCogni) return { phrase, terms: highlightTerms, colors: highlightColors };
    const ev = cogniEvidence.get(id);
    if (ev && ev.region !== 'US' && ev.snippet) {
      return { phrase: ev.snippet, terms: [ev.snippet], colors: ['#fde047'] as (string | undefined)[] };
    }
    return { phrase: '', terms: [] as string[], colors: [] as (string | undefined)[] };
  };
  // Cogni non-US: the evidence section as a concept → DrugColumn's PDF scroll
  // effect drives the viewer to it. (US uses scrollToSectionNumber instead so we
  // don't narrow the section view.)
  const cogniConcept = (id: number): ConceptEntry | null => {
    if (!isCogni) return null;
    const ev = cogniEvidence.get(id);
    if (!ev || ev.region === 'US' || !ev.section) return null;
    return {
      key: '__cogni_evidence',
      label: ev.section,
      universal_order: null,
      sections: { [ev.region]: [ev.section] },
      notes: {},
    };
  };
  // Per-column hierarchical section selection (the section_numbers to SHOW for
  // each drug; undefined = all). Replaces the old single global section
  // dropdown so each column — and, in phase 2, each region — is scoped
  // independently. Keyed by drug_id; survives panel/star changes.
  const [colSections, setColSections] = useState<Record<number, string[]>>({});
  const setDrugSections = (id: number, next: string[]) =>
    setColSections((p) => ({ ...p, [id]: next }));
  // Deletes the key: `narrowedSections` treats `[]` as a real manual pick.
  const clearDrugSections = (id: number) =>
    setColSections((p) => {
      if (!(id in p)) return p;
      const { [id]: _dropped, ...rest } = p;
      return rest;
    });
  // Retained only as the searched-section hint for deep links.
  const section = searchedSection || '';

  // Universal Section: one pick resolves per region in every panel (US §5 ↔ EU
  // 4.4 ↔ CA §7). The vocabulary and the map come from /api/section-concepts —
  // the same table CEPI's read_label_section resolves through — so nothing here
  // hardcodes a concept or a region. A per-column manual section selection
  // still overrides it (manual > universal > all).
  // Grouping inherited from the search page (?group=). The rail groups the
  // same way the results table did, so the view the user built there survives
  // the jump into Compare.
  const railGroupBy = (params.get('group') ?? 'none') as GroupByOption;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const concepts = useSectionConcepts();
  // `usec` is a comma-joined list of universal-section keys (multi-select). An
  // old single-value deep link (?usec=warnings_and_precautions) parses to one
  // key, so existing links keep working.
  const universalKeys = (params.get('usec') ?? '').split(',').filter(Boolean);
  const universalConcepts = universalKeys
    .map((k) => concepts.find((c) => c.key === k))
    .filter((c): c is ConceptEntry => !!c);
  // A Universal Section pick is the user's explicit choice, so it wins over the
  // Cogni evidence section (only the default landing). Flag OFF = the old
  // order, where the evidence silently overrode the pick on non-US panels. Cogni
  // is always exactly one synthetic one-region concept.
  const panelConcepts = (id: number): ConceptEntry[] => {
    const cog = cogniConcept(id);
    return universalConcepts.length ? universalConcepts : (cog ? [cog] : []);
  };

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v === null || v === '') next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };
  const writeStars = (ids: number[]) => {
    const uniq = Array.from(new Set(ids)).slice(0, MAX_STARS);
    setParam('stars', uniq.length ? uniq.join(',') : null);
  };
  // ★ = the SHORTLIST (save + share). Independent of the
  // compare panels — capped at MAX_STARS (the rail disables ☆ at the cap), no
  // panel-cap. Toggling a star NEVER touches a panel and adding a panel NEVER stars.
  const toggleStar = (id: number) =>
    writeStars(stars.includes(id) ? stars.filter((s) => s !== id) : [...stars, id]);

  // ── bulk ★ ── tick several rail rows, star them in ONE action ───────────
  // The ticks are EPHEMERAL page state on purpose: ?stars= stays the shareable
  // shortlist, and a tick box is only the fast way to build it. Single-★
  // clicking a row is untouched. 
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);

  // Update several URL params in ONE history entry — needed when a single action
  // changes both the panel set and the slot count (setParam/writePanels each
  // snapshot `params`, so calling them back-to-back would race).
  const setParamsMulti = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  // Mode flip (spec §5.8 -> §5.7/AC15): loaded pages disagree on on-demand, so
  // drop the cached pages, restart at offset 0 and clear the URL sort. The
  // prefix omits the sort, so every sort variant of this search is covered,
  // including a still-fresh unsorted entry.
  useEffect(() => {
    if (!railModesDisagree || isCogni) return;
    // The rail's OWN (gated) query key: a non-admin's sanitized replay of an
    // admin request lives under the sanitized request, not ``kwRequest``.
    const prefix = ['keyword-search-infinite', rail.request, pageSize, 'on_demand'];
    try { sessionStorage.removeItem(storageKey('kw-sort')); } catch { /* ignore */ }
    if (railSort) {
      queryClient.removeQueries({ queryKey: prefix });
      setParamsMulti({ [KW_SORT_PARAM]: null });
    } else {
      // resetQueries refetches the active query from offset 0; removing first
      // would leave nothing to reset.
      void queryClient.resetQueries({ queryKey: prefix });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railModesDisagree]);

  // ── the compare set = the PANEL drugs (≤100), independent of ★ stars ─────────
  // Click a rail row → toggle it in/out of the panels (any drug, any order).
  // Seeded to the first 2 results (default 2-up A/B). Drag-drop also fills slots.
  const writePanels = (ids: number[]) => {
    const uniq = Array.from(new Set(ids)).slice(0, MAX_PANELS);
    setParam('panels', uniq.length ? uniq.join(',') : null);
  };
  const compareSet: number[] = panels;
  const writeCompareSet = (ids: number[]) => writePanels(ids);
  // Click-to-add/remove a drug from the compare panels (the rail's primary
  // action). Adds to the next slot (cap 100); removes if already shown.
  const togglePanel = (id: number) => {
    if (panels.includes(id)) writePanels(panels.filter((p) => p !== id));
    else if (panels.length < MAX_PANELS) writePanels([...panels, id]);
  };

  // Seed the first 2 results as the default A/B pair on first load (once per
  // mount). A URL that already carries ?panels= (reload / share) skips the seed.
  // Seeding is an initial-load decision: a page that opened WITH columns (URL,
  // saved list, migrated stars link) never seeds, even after they are removed.
  const seededRef = useRef(params.has('panels') || oldStarsLink);
  useEffect(() => {
    if (seededRef.current || hasViewParam) return; // wait for the legacy-link migration
    if (panels.length === 0 && filteredResults.length > 0) {
      seededRef.current = true;
      writePanels(filteredResults.slice(0, 2).map((r) => r.drug_id));
    }
  }, [panels.length, filteredResults, hasViewParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Panel SLOTS (2–100): an explicit count (?cols), never below the # of panel
  // drugs. "+ Add panel" bumps it (creating an EMPTY slot to fill from the rail);
  // clicking a rail drug directly also grows it. Each slot i shows panels[i] or,
  // when empty (slots > panels), a "pick a drug" placeholder.
  const colsParam = Math.max(2, Math.min(MAX_PANELS, Number(params.get('cols') ?? 2) || 2));
  const slots = Math.max(2, Math.min(MAX_PANELS, Math.max(colsParam, panels.length)));
  const panelIds = compareSet.slice(0, slots); // filled drugs (length ≤ slots)
  const canAddPanel = slots < MAX_PANELS;
  const addPanel = () => {
    scrollStripToEnd.current = true;
    setParam('cols', String(Math.min(MAX_PANELS, slots + 1)));
  };
  // When a panel/slot is added, smooth-scroll the strip to reveal the new
  // (rightmost) column — shows it AND signals the strip scrolls horizontally.
  // Armed ONLY by "+ Add panel": bulk loads (saved list, migration)
  // stay at the start so column 1 mounts first.
  const panelStripRef = useRef<HTMLDivElement>(null);
  const scrollStripToEnd = useRef(false);
  useEffect(() => {
    if (!scrollStripToEnd.current) return;
    scrollStripToEnd.current = false;
    const el = panelStripRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
  }, [slots]);
  // drugId → identity color for the drugs currently in panels (by slot). Shared
  // by the panel top-bar/dot and the matching left-rail row.
  const panelColorOf = (id: number): string | undefined => {
    const i = panelIds.indexOf(id);
    return i >= 0 ? slotColor(i) : undefined;
  };
  const slotLetterOf = (id: number): string | undefined => {
    const i = panelIds.indexOf(id);
    return i >= 0 ? slotLabel(i) : undefined;
  };
  // Replace the drug in panel slot `slotIndex` with `draggedId` (drag-to-fill).
  // If the dragged id is already elsewhere, move it into the slot instead.
  const replaceSlot = (slotIndex: number, draggedId: number) => {
    const target = slotIndex;
    const next = compareSet.filter((id) => id !== draggedId);
    const insertAt = compareSet[target] != null
      ? next.indexOf(compareSet[target]!)
      : next.length;
    const at = insertAt < 0 ? next.length : insertAt;
    next.splice(at, 0, draggedId);
    if (compareSet[target] != null && compareSet[target] !== draggedId) {
      const rm = next.indexOf(compareSet[target]!);
      if (rm >= 0) next.splice(rm, 1);
    }
    writeCompareSet(next.slice(0, MAX_PANELS));
  };
  // Remove a panel SLOT (filled or empty): drop its drug (if any) AND shrink the
  // slot count by one — atomic so we don't leave a stray trailing empty.
  const removePanel = (slotIndex: number) => {
    const id = panels[slotIndex];
    const nextPanels = id != null ? panels.filter((p) => p !== id) : panels;
    const nextCols = Math.max(2, Math.min(MAX_PANELS, slots - 1));
    setParamsMulti({
      panels: nextPanels.length ? Array.from(new Set(nextPanels)).join(',') : null,
      cols: String(nextCols),
    });
  };

  // When this session was restored from a saved list, editing + re-saving should
  // UPDATE that same list (not create a new one). Carried in the URL so it
  // survives in the URL.
  const editListId = Number(params.get('listId')) || null;
  const editListName = params.get('listName');
  // The rail's rows. Results tab = the (fresh) search results, narrowed by any
  // Refine chips carried over from the results table. Starred tab = the starred drugs in star
  // order: the loaded row when there is one, else a minimal row that RailRow
  // fills via its own per-drug fetch.
  const starredRows: KeywordSearchResultItem[] = stars.map(
    (id) =>
      resultLookup.find((r) => r.drug_id === id) ??
      ({ drug_id: id, brand_name: null, generic_name: null, manufacturer: null, region: null, total_matches: 0, highlights: [] } as unknown as KeywordSearchResultItem),
  );
  const tabRows = activeTab === 'starred' ? starredRows : filteredResults;
  // Rows probed for hydrated display metadata: every starred id without a loaded
  // row, plus every Results row whose own fields are incomplete (RailRow's own
  // condition — Cogni rows carry no manufacturer). Same ['drug', id] key as
  // RailRow, so no extra request.
  const probeIds = useMemo(() => {
    const ids = new Set<number>();
    for (const id of stars) if (!resultLookup.some((r) => r.drug_id === id)) ids.add(id);
    for (const r of results) if (isCogni || !r.manufacturer || !r.brand_name) ids.add(r.drug_id);
    return Array.from(ids);
  }, [stars, results, resultLookup, isCogni]);
  const probeSet = useMemo(() => new Set(probeIds), [probeIds]);
  // Display metadata of a row: null while unresolved (pending or failed) so the
  // filter never hides a row it cannot see yet.
  const metaOf = (r: KeywordSearchResultItem): FilterMeta => {
    if (!probeSet.has(r.drug_id)) return r;
    const m = rowMeta[r.drug_id];
    if (!m || m.status !== 'ok') return null;
    return {
      brand_name: r.brand_name ?? m.brand_name ?? null,
      generic_name: r.generic_name ?? m.generic_name ?? null,
      manufacturer: r.manufacturer ?? m.manufacturer ?? null,
    };
  };
  const railItems: KeywordSearchResultItem[] = railFilter.trim()
    ? tabRows.filter((r) => matchesRailFilter(metaOf(r), railFilter))
    : tabRows;

  // The rail's rows in the order they are actually ON SCREEN (a collapsed group
  // hides its rows). "Select all" works off THIS list, so it can never touch a
  // row the user cannot see.
  const railGroups = groupRows(railItems, railGroupBy, usFdaVisible);
  const visibleRailIds: number[] = (railGroups
    ? railGroups.flatMap((g) => (collapsedGroups.has(g.key) ? [] : g.rows))
    : railItems
  ).map((r) => r.drug_id);
  const selectedVisible = visibleRailIds.filter((id) => selectedIds.has(id));
  const selectedUnstarred = selectedVisible.filter((id) => !stars.includes(id));
  const selectedStarred = selectedVisible.filter((id) => stars.includes(id));
  // Selecting is capped at MAX_STARS, the same ceiling ?stars= has: a selection
  // you could not star in full would only fail later, at the star click.
  const atSelectCap = selectedIds.size >= MAX_STARS;
  const onSelectToggle = (id: number) => {
    setSelectedIds((prev) => toggleSelected(prev, id, MAX_STARS));
    setBulkMessage(null);
  };
  const selectAllVisible = () => {
    const res = selectAllUpTo(visibleRailIds, MAX_STARS);
    setSelectedIds(res.selected);
    setBulkMessage(
      res.skipped
        ? `Selected the first ${res.selected.size} — ${res.skipped} more could not be selected (limit ${MAX_STARS}).`
        : null,
    );
  };
  // Star every ticked row at once. The selection SURVIVES the action so the
  // result is visible (the rows go gold) and "Unstar" can undo it.
  const starSelected = () => {
    const res = applyBulkStar(stars, selectedVisible, MAX_STARS);
    writeStars(res.stars);
    setBulkMessage(describeBulkStar(res, MAX_STARS));
  };
  const unstarSelected = () => {
    const removed = selectedStarred.length;
    writeStars(applyBulkUnstar(stars, selectedVisible));
    setBulkMessage(removed ? `Unstarred ${removed}.` : null);
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkMessage(null);
  };

  const openDrug = (drugId: number) => {
    const hl = resultLookup.find((r) => r.drug_id === drugId)?.highlights?.[0] ?? null;
    const p = buildDrugHighlightParams(hl, {
      keyword: phrase,
      keywordSource: !isCogni,
      exactPhrase: true, fallbackSection: section || null, corpus: !isCogni && kwRequest?.sources?.length === 1 ? kwRequest.sources[0] : jpJaCorpus,
    });
    navigate(`/drug/${drugId}?${p.toString()}`);
  };

  // Export/Share/chat act on the COLUMNS only (one path). The labels say what
  // that is: "starred" exactly when the columns are the starred set.
  const nCols = panelIds.length;
  const starSet = new Set(stars);
  const colsAreStars = nCols === starSet.size && panelIds.every((id) => starSet.has(id));
  const exportLabel = colsAreStars ? `Export starred (${nCols})` : `Export ${nCols} columns`;
  const shareLabel = colsAreStars ? `Share starred (${nCols})` : `Share ${nCols} columns`;

  // ── Saved starred lists ───────────────────────────────────────────────────
  // The starred drugs to save, with names inline so an old list renders without
  // re-fetching. Stars are chosen from the rail, so they're in `results`.
  const starredDrugsForSave = (): SavedListDrug[] =>
    stars.map((id) => {
      const r = resultLookup.find((x) => x.drug_id === id) ?? null;
      return {
        drug_id: id,
        brand_name: r?.brand_name ?? null,
        generic_name: r?.generic_name ?? null,
        region: r?.region ?? null,
      };
    });
  // The search a saved list is tied to (for back-reference + restore).
  const savedSearchContext = (): SearchDescriptor => ({
    mode: 'keyword',
    label: phrase || highlightTerms.join(' ') || 'Starred list',
    payload: (kwRequest ?? {}) as Record<string, unknown>,
  });
  // Build the COMPLETE comparison content for each drug (used by both Export
  // and Share-snapshot) — the shared gather (utils/exportGather), fed what this
  // page knows: the search hit per column, per-column sections, Cogni evidence.
  // Per column, the active concept pick(s) its on-screen filter falls back to
  // when it has no manual pick. Sent region-agnostic: the gather resolves them
  // against each drug's fetched region (exactly as DrugColumn does), so columns
  // never mounted or absent from the search rows still export what the dropdown
  // shows.
  const colConceptsFor = (ids: number[]): Record<number, ConceptEntry[] | undefined> =>
    Object.fromEntries(ids.map((id) => [id, panelConcepts(id).length ? panelConcepts(id) : undefined]));
  const gatherContext = (ids: number[]): GatherContext => ({
    hits: new Map(ids.map((id) => [id, hitFromResult(id, resultLookup.find((r) => r.drug_id === id), !isCogni)])),
    coloredTerms,
    colSections,
    colConcepts: colConceptsFor(ids),
    isCogni,
    evidence: Object.fromEntries(ids.map((id) => [id, cogniEvidence.get(id)?.chunkText ?? null])),
    usFdaVisible,
  });
  const gatherExportDrugs = (
    ids: number[],
    opts: { signal?: AbortSignal; onDrugDone?: (done: number) => void; onLayoutProgress?: (done: number, total: number) => void } = {},
  ): Promise<ExportDrug[]> => gatherDrugs(ids, gatherContext(ids), browserGatherClient, { ...opts, concurrency: 4 });

  /** One estimate item per column: which corpus + document the column exports
   *  (the same resolution gatherExportDrugs does, from the search hit). */
  const estimateItems = (ids: number[]): EstimateItem[] =>
    ids.map((id) => {
      const h = resultLookup.find((r) => r.drug_id === id)?.highlights?.[0];
      const source = String(h?.source ?? 'label');
      return {
        drug_id: id,
        source,
        ...(source !== 'fda_letter' && h?.document_id != null ? { document_id: h.document_id } : {}),
        ...(source === 'fda_letter' && h?.section_id != null ? { letter_section_id: h.section_id } : {}),
      };
    });

  const gatherForExport = async (ids: number[], signal: AbortSignal, exportTerms: typeof coloredTerms | undefined) => {
    let gathered: ExportDrug[];
    try {
      gathered = await gatherExportDrugs(ids, {
        signal,
        onDrugDone: (done) => setExportPct(stagePercent('gather', done, ids.length)),
        onLayoutProgress: (done, total) => setLayoutProgress({ done, total }),
      });
    } finally {
      setLayoutProgress(null);
    }
    return applyUserHighlights(gathered, ids, userHighlights.highlights, phrase, exportTerms);
  };

  /** Build the file in this tab (small exports, and the fallback). */
  const runBrowserExport = async (format: ExportFormat, ids: number[], estimate: ExportEstimate | null) => {
    const controller = new AbortController();
    exportAbort.current = controller;
    const timer = new ExportStageTimer();
    const started = performance.now();
    let drugs: ExportDrug[] = [];
    let outcome: 'complete' | 'cancelled' | 'error' = 'error';
    setExporting(true);
    setExportPct(0);
    try {
      const exportTerms = coloredTerms.length > 1 ? coloredTerms : undefined;
      timer.start('gather');
      drugs = await gatherForExport(ids, controller.signal, exportTerms);
      const download = await exportDownloader(format);
      await download(drugs, phrase, section || undefined, exportTerms, {
        signal: controller.signal,
        onProgress: setExportPct,
        timer,
      });
      outcome = 'complete';
    } catch (e) {
      if (!isAbortError(e)) throw e; // Cancel is not an error
      outcome = 'cancelled';
    } finally {
      timer.stop();
      exportAbort.current = null;
      setExporting(false);
      setExportPct(null);
      exportJobsApi.telemetry({
        path: 'client',
        format,
        outcome,
        drugs: ids.length,
        pdf_pages: drugs.reduce((n, d) => n + (d.pdf ? pagesToEmbed(d.pdf).length : 0), 0),
        total_ms: Math.round(performance.now() - started),
        stage_ms: timer.timings as Record<string, number>,
        ...(typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? { cores: navigator.hardwareConcurrency } : {}),
        ...(estimate ? { estimated_seconds: estimate.estimated_seconds, cost_model_version: estimate.cost_model_version } : {}),
      });
    }
  };

  /** Hand a big export to the server: create the job (joins an identical one
   *  already running). With server gather the worker then collects the drugs
   *  itself — the job is already queued. Otherwise gather + gzip + upload the
   *  drugs straight to GCS, then submit. The page then polls the job;
   *  /export/<id> shows it. */
  const runServerExport = async (format: 'xlsx' | 'docx' | 'html', ids: number[], estimate: ExportEstimate) => {
    const exportTerms = coloredTerms.length > 1 ? coloredTerms : undefined;
    const manual = Object.fromEntries(ids.filter((id) => userHighlights.highlights[id]?.length).map((id) => [String(id), userHighlights.highlights[id]!]));
    const cols = Object.fromEntries(ids.filter((id) => colSections[id]).map((id) => [String(id), colSections[id]!]));
    // What the worker needs to gather the drugs itself (the same context the
    // browser gather uses). Cogni evidence only where there is some.
    const ctx = gatherContext(ids);
    const gather: ExportGatherSpec = {
      hits: ids.map((id) => ctx.hits.get(id)!),
      highlight_terms: coloredTerms,
      is_cogni: isCogni,
      evidence: Object.fromEntries(ids.flatMap((id) => (ctx.evidence[id] ? [[String(id), ctx.evidence[id]!]] : []))),
      export_v2: true,
      us_fda_visible: usFdaVisible,
    };
    const colConcepts = Object.fromEntries(ids.flatMap((id) => (ctx.colConcepts?.[id] ? [[String(id), ctx.colConcepts[id]!]] : [])));
    let job;
    try {
      job = await exportJobsApi.create({
        format,
        drug_ids: ids,
        keyword: phrase,
        ...(section ? { section } : {}),
        ...(exportTerms ? { colored_terms: exportTerms } : {}),
        col_sections: cols,
        col_concepts: colConcepts,
        manual_highlights: manual,
        estimated_seconds: estimate.estimated_seconds,
        return_url: location.pathname + location.search,
        gather,
      });
    } catch (e) {
      const status = (e as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      if (status === 429) {
        const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
        setExportMessage({ kind: 'error', text: detail ?? 'You already have exports running. Please wait for one to finish.' });
        return;
      }
      if (estimate.browser_fallback_allowed) {
        setExportMessage({ kind: 'info', text: 'The export server is unavailable — preparing in your browser instead.' });
        return runBrowserExport(format, ids, estimate);
      }
      setExportMessage({ kind: 'error', text: 'The export server is unavailable and this export is too large to build in your browser. Try again later, or export fewer drugs.' });
      return;
    }
    setServerJob({ id: job.job_id, status: job.status, pct: job.progress_pct });
    if (job.server_gather && !job.deduplicated) return; // queued; the worker gathers
    if (job.deduplicated || !job.upload_url) {
      setExportMessage({ kind: 'info', text: 'This export is already being prepared.' });
      return;
    }
    const controller = new AbortController();
    exportAbort.current = controller;
    setExporting(true);
    setExportPct(0);
    let fallback = false;
    try {
      const drugs = await gatherForExport(ids, controller.signal, exportTerms);
      const payload: ExportPayload = {
        version: 1,
        format,
        drugs,
        keyword: phrase,
        ...(section ? { section } : {}),
        ...(exportTerms ? { coloredTerms: exportTerms } : {}),
      };
      await uploadPayload(job.upload_url, job.upload_content_type ?? 'application/gzip', await gzipJson(payload), controller.signal);
      const submitted = await exportJobsApi.submit(job.job_id);
      setServerJob({ id: submitted.job_id, status: submitted.status, pct: submitted.progress_pct });
    } catch (e) {
      await exportJobsApi.cancel(job.job_id).catch(() => {});
      setServerJob(null);
      if (isAbortError(e)) return;
      if (estimate.browser_fallback_allowed) {
        setExportMessage({ kind: 'info', text: 'Sending the export to the server failed — preparing in your browser instead.' });
        fallback = true;
      } else {
        setExportMessage({ kind: 'error', text: 'Sending the export to the server failed. Please try again.' });
      }
    } finally {
      exportAbort.current = null;
      setExporting(false);
      setExportPct(null);
    }
    // After the finally above, so it can't reset the fallback export's state.
    if (fallback) await runBrowserExport(format, ids, estimate);
  };

  const onExport = async (format: ExportFormat) => {
    const ids = panelIds;
    if (!ids.length) return;
    setExportOpen(false); // the button itself shows the progress
    setExportMessage(null);
    if (format === 'xls') return runBrowserExport(format, ids, null);
    setExporting(true);
    setExportPct(null); // "Preparing…" while estimating
    let estimate: ExportEstimate | null = null;
    try {
      estimate = await exportJobsApi.estimate(format, estimateItems(ids));
    } catch {
      /* no estimate: build in the browser */
    }
    setExporting(false);
    if (!estimate || !estimate.server_available || estimate.recommended_path === 'client') {
      return runBrowserExport(format, ids, estimate);
    }
    return runServerExport(format, ids, estimate);
  };

  // Poll the server job every 2 s until it finishes (the plan's §6.2 poll).
  const serverJobId = serverJob?.id;
  const serverJobActive = !!serverJob && ACTIVE_EXPORT_STATUSES.includes(serverJob.status);
  useEffect(() => {
    if (!serverJobId || !serverJobActive) return;
    let stopped = false;
    const t = setInterval(async () => {
      try {
        const j = await exportJobsApi.get(serverJobId);
        if (!stopped) setServerJob({ id: j.job_id, status: j.status, pct: j.progress_pct });
      } catch { /* transient; keep polling */ }
    }, 2000);
    return () => { stopped = true; clearInterval(t); };
  }, [serverJobId, serverJobActive]);

  // Cogni-style Share: snapshot the complete comparison server-side, return a
  // public /snapshot/<token> link anyone can open (SnapshotPage renders it).
  const onShare = async () => {
    const ids = panelIds;
    if (!ids.length) return;
    setSharing(true);
    setShareWarning(null);
    try {
      // ALL cases → side-by-side panel snapshot (keyword_compare), matching the
      // retired-matrix decision: the shared snapshot mirrors the scrollable
      // N-column panel view (SnapshotKeywordCompare), US + non-US alike. PDF drugs
      // carry their pages (zoomable viewer); drop their redundant text blocks to
      // keep the snapshot payload small (a 100-page doc's text is large).
      const gathered = await gatherExportDrugs(ids);
      // NEVER bake a dead/empty column into a permanent public snapshot. Drop a
      // drug when it has no authoritative content to show: keep it only if it has
      // a PDF, OR has text blocks that are NOT stale (unreachable = getDrug failed
      // → deleted drug_id from a stale keyword posting; its blocks are stale
      // search-hit excerpts). This covers both the empty case and the
      // deleted-drug-with-leftover-highlights case. Tell the user what was left out.
      const isLoadable = (d: ExportDrug) => !!d.pdf || (d.blocks.length > 0 && !d.unreachable);
      const drugs = gathered.filter(isLoadable);
      const dropped = gathered.filter((d) => !isLoadable(d));
      if (!drugs.length) {
        setShareWarning(null);
        setShareUrl('all-unloadable');
        return;
      }
      const snapDrugs = drugs.map((d) => (d.pdf ? { ...d, blocks: [] } : d));
      const res = await authApi.createSnapshot({
        query: `Keyword Compare: “${phrase}” — ${drugs.map((d) => d.name).join(' vs ')}`.slice(0, 280),
        drug_results: drugs.map((d) => ({ name: d.name, subtitle: d.subtitle })),
        metadata: {
          type: 'keyword_compare',
          keyword: phrase,
          section: section || null,
          drugs: snapDrugs,
          ...(coloredTerms.length > 1 ? { coloredTerms } : {}),
        },
        result_count: drugs.length,
      });
      if (dropped.length) {
        setShareWarning(
          `${dropped.length} drug${dropped.length > 1 ? 's' : ''} couldn’t be loaded and ` +
            `${dropped.length > 1 ? 'were' : 'was'} left out of the snapshot: ` +
            `${dropped.map((d) => d.name).join(', ')}.`,
        );
      }
      setShareUrl(`${window.location.origin}/snapshot/${res.share_token}`);
      setCopied(false);
    } catch {
      setShareUrl('error');
    } finally {
      setSharing(false);
    }
  };

  // Footer line of the rail: the starred drugs' names (hydrated when not loaded).
  const starNames = starredRows.map((r) => metaOf(r)?.brand_name || r.brand_name || `Drug ${r.drug_id}`).join(', ');
  const starFooterText = `${stars.length} starred · ${starNames}`;
  const backLabel = compareBackLabel(isCogni, railCount(filteredResults.length, hasMoreResults));

  // Chat context: the drugs in the columns. Fewer than two is not a comparison, so nothing is published (D3).
  const comparedDrugs = panelIds.map((id) => {
    const r = resultLookup.find((x) => x.drug_id === id);
    return { drug_id: id, brand_name: r?.brand_name ?? null, generic_name: r?.generic_name ?? null };
  });
  usePublishChatContext(comparedDrugs.length >= 2 ? {
    context: { mode: 'drug_compare', drug_compare: { drugs: comparedDrugs } },
    exportDrugNames: comparedDrugs
      .map((d) => d.brand_name ?? d.generic_name)
      .filter((n): n is string => !!n),
  } : null);

  return (
    <div className="flex h-full flex-col bg-ground">
      {/* Hydrates rail rows' display metadata (filter + footer names). Outside the
          tab content so it runs whichever tab is active. */}
      {probeIds.map((id) => <DrugMetaProbe key={id} id={id} onMeta={onMeta} />)}
      {/* top bar */}
      <div className="flex items-center gap-3 border-b border-line bg-white px-4 py-2.5">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-accent-deep hover:underline"
        >
          {backLabel}
        </button>
        <h1 className="font-display text-[20px] font-medium text-ink">Compare labels</h1>

        <div className="mx-1 h-5 w-px bg-line" />

        {/* One section name, every region: picking "Warnings and Precautions"
            sends the US panel to §5, the EU panel to 4.4, the CA panel to §7. */}
        <UniversalSectionSelect
          concepts={concepts}
          values={universalKeys}
          onChange={(keys) => {
            // A new pick applies to EVERY column: drop the manual per-column picks.
            setColSections({});
            setParam('usec', keys.length ? keys.join(',') : null);
          }}
        />

        <div className="ml-auto flex items-center gap-2">
          <button onClick={onShare} disabled={sharing} className="btn-secondary" title="Create a shareable snapshot link to this comparison">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            {sharing ? 'Sharing…' : shareLabel}
          </button>
          <div className="relative">
            <button onClick={() => setExportOpen((o) => !o)} className="btn-secondary" title="Download side-by-side as HTML, Excel or Word (offline, keyword highlighted)">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting
                ? layoutProgress
                  ? `Formatting ${layoutProgress.done} of ${layoutProgress.total} sections…`
                  : exportPct === null
                  ? 'Preparing…'
                  : stageForPercent(exportPct) === 'build'
                    ? `${EXPORT_STAGE_LABEL.build}…`
                    : `${EXPORT_STAGE_LABEL[stageForPercent(exportPct)]}… ${exportPct}%`
                : exportLabel}
              <svg className="h-3.5 w-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {exporting && (
              <button
                onClick={() => exportAbort.current?.abort()}
                className="btn-secondary ml-1"
                title="Stop this export"
              >
                Cancel
              </button>
            )}
            {exporting && exportPct !== null && (
              <div className="absolute left-0 right-0 -bottom-1 h-0.5 overflow-hidden rounded bg-line-subtle" aria-hidden>
                <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${exportPct}%` }} />
              </div>
            )}
            {serverJob && serverJob.status !== 'cancelled' && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-ctl border border-line bg-white px-2 py-1.5 text-xs text-ink-2">
                <a
                  href={`/export/${serverJob.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-accent hover:underline"
                  title="Open the export page: progress, preview and download"
                >
                  {serverJob.status === 'complete'
                    ? 'Export ready — View'
                    : serverJob.status === 'error' || serverJob.status === 'expired'
                      ? 'Export failed — details'
                      : serverJob.status === 'awaiting_upload'
                        ? 'Sending to server…'
                        : `Exporting on server… ${serverJob.pct}%`}
                </a>
                {!ACTIVE_EXPORT_STATUSES.includes(serverJob.status) && (
                  <button onClick={() => setServerJob(null)} className="text-ink-3 hover:text-ink-2" title="Dismiss" aria-label="Dismiss">×</button>
                )}
              </span>
            )}
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 z-30 mt-1 w-44 rounded border border-line bg-white p-1 text-sm shadow-pop">
                  <button onClick={() => onExport('html')} disabled={exporting} className="block w-full rounded-ctl px-2 py-1.5 text-left hover:bg-panel disabled:opacity-50">
                    Download HTML <span className="text-[10px] text-ink-3">(offline)</span>
                  </button>
                  <button onClick={() => onExport('xlsx')} disabled={exporting} className="block w-full rounded-ctl px-2 py-1.5 text-left hover:bg-panel disabled:opacity-50">
                    Download Excel
                  </button>
                  <button onClick={() => onExport('docx')} disabled={exporting} className="block w-full rounded-ctl px-2 py-1.5 text-left hover:bg-panel disabled:opacity-50">
                    Download Word
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {exportMessage && (
        <div className={`flex items-center justify-between gap-3 border-b px-3 py-1.5 text-xs ${exportMessage.kind === 'error' ? 'border-red-100 bg-red-50 text-red-700' : 'border-accent-ring bg-accent-tint text-accent-deep'}`}>
          <span>{exportMessage.text}</span>
          <button onClick={() => setExportMessage(null)} className="opacity-60 hover:opacity-100" aria-label="Dismiss">×</button>
        </div>
      )}
      {(coloredTerms.length > 1 || userHighlights.count > 0) && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 border-b border-line-subtle bg-white px-3 py-1.5">
          <HighlightLegend terms={coloredTerms} />
          <UserHighlightsChip count={userHighlights.count} onClear={userHighlights.clear} />
        </div>
      )}
      <UserHighlighter
        highlights={userHighlights.highlights}
        onAdd={userHighlights.add}
        onRemove={userHighlights.remove}
      />

      <div className="flex min-h-0 flex-1">
        {/* left rail — Results tab: the (fresh) search RESULTS (★ to shortlist,
            click/drag into panels). Starred tab: the shortlist, plus a footer to
            save it / show it in the columns. A saved list opens on the Starred tab.
            Tabs + filter on top and the footer
            at the bottom are fixed; only the list (railRef) scrolls. */}
        {(
          <aside className="flex w-64 shrink-0 flex-col border-r border-line bg-white">
            <div className="shrink-0 space-y-2 p-3 pb-2">
              <div className="flex items-center justify-between gap-1">
                <div role="tablist" className="flex items-center gap-3 text-xs font-medium">
                  <button
                    role="tab"
                    aria-selected={activeTab === 'results'}
                    onClick={() => setRailTab('results')}
                    className={activeTab === 'results' ? 'border-b-2 border-accent pb-0.5 text-ink' : 'pb-0.5 text-ink-3 hover:text-ink-2'}
                  >
                    {`Results · ${railCount(filteredResults.length, hasMoreResults)}`}
                  </button>
                  <button
                    role="tab"
                    aria-selected={activeTab === 'starred'}
                    onClick={() => setRailTab('starred')}
                    className={activeTab === 'starred' ? 'border-b-2 border-accent pb-0.5 text-ink' : 'pb-0.5 text-ink-3 hover:text-ink-2'}
                  >
                    {`★ Starred · ${stars.length}`}
                  </button>
                </div>
                {/* Multi-select: build the ★ shortlist in one action instead of
                    one click per row. */}
                {railItems.length > 0 && (
                  <button
                    onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                    data-testid="rail-select-toggle"
                    aria-pressed={selectMode}
                    title="Select several drugs, then star them together"
                    className={`rounded-ctl border px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
                      selectMode
                        ? 'border-accent-ring bg-accent-tint text-accent-deep'
                        : 'border-line text-ink-2 hover:bg-panel'
                    }`}
                  >
                    {selectMode ? 'Done' : 'Select'}
                  </button>
                )}
              </div>
              <input
                type="search"
                aria-label="Filter results"
                placeholder="Filter results"
                value={railFilter}
                onChange={(e) => setRailFilter(e.target.value)}
                className="h-8 w-full rounded-ctl border border-line-input bg-white px-2 text-xs text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-ring"
              />
            </div>
            <div
              ref={railRef}
              data-testid="rail-list"
              onScroll={onRailScroll}
              className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
            >
            {selectMode && (
              <RailBulkStarBar
                selectedCount={selectedVisible.length}
                visibleCount={visibleRailIds.length}
                allSelected={
                  visibleRailIds.length > 0
                  && (selectedVisible.length === visibleRailIds.length || selectedVisible.length >= MAX_STARS)
                }
                unstarredCount={selectedUnstarred.length}
                starredCount={selectedStarred.length}
                onSelectAll={selectAllVisible}
                onClear={() => { setSelectedIds(new Set()); setBulkMessage(null); }}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onExit={exitSelectMode}
                message={bulkMessage}
                capNote={selectionCapNote(selectedIds.size, MAX_STARS)}
              />
            )}
            {activeTab === 'starred' && stars.length >= MAX_STARS && (
              <div className="mb-2 rounded border border-warn-line bg-warn-bg px-2 py-1.5 text-xs text-warn-ink">
                Limit reached: you can star up to {MAX_STARS} drugs. Remove one to star another.
              </div>
            )}
            {activeTab === 'results' && railFilterActive && (
              <div data-testid="rail-refine-filters" className="mb-2 flex flex-wrap items-center gap-1 text-[11px]">
                <span className="text-ink-3">Filtered:</span>
                {railFilterLabels.map((label) => (
                  <span key={label} className="rounded-ctl bg-accent-tint px-1.5 py-0.5 text-accent-deep">
                    {label}
                  </span>
                ))}
                <button
                  onClick={() => (cogniTableFilters
                    ? setParamsMulti(Object.fromEntries(COGNI_TABLE_FILTER_PARAMS.map((k) => [k, null])))
                    : setParam('refine', null))}
                  className="text-ink-2 underline hover:text-ink"
                  title="Show all search results in Compare (the results page keeps its filter)"
                >
                  Clear
                </button>
              </div>
            )}
            {railItems.length === 0 && (
              <div className="text-xs text-ink-3">
                {activeTab === 'starred'
                  ? railFilter.trim() && tabRows.length > 0
                    ? 'No starred drug matches this filter.'
                    : 'Star (★) drugs in the list to shortlist them.'
                  : railFilter.trim() && tabRows.length > 0
                  ? hasMoreResults
                    ? `No match in the first ${results.length} results. Load more to keep looking.`
                    : 'No results match this filter.'
                  : !isCogni && rail.isLoading
                  // e.g. Clear: the page size changes, so the rail refetches from the top.
                  ? 'Loading results…'
                  : railFilterActive && results.length > 0
                  ? ((isCogni ? cogniLoadingMore : rail.isFetchingNextPage)
                      || ((isCogni ? cogniHasMore : rail.hasNextPage) && !railAutoFillCapped)
                    ? 'Looking for results that match these filters…'
                    : (isCogni ? cogniHasMore : rail.hasNextPage)
                      ? `No match in the first ${results.length} results. Load more to keep looking.`
                      : 'No results match these filters.')
                  : isCogni ? 'Run a Cogni search first, then open Compare.' : 'Run a keyword search first, then open Compare.'}
              </div>
            )}
            {railItems.length > 0 && (
              <div className="mb-2 space-y-0.5 text-[10px] leading-tight text-ink-3">
                <div><b>Click</b> a drug → add it to the comparison (up to 100)</div>
                <div>★ = separate shortlist (save, share)</div>
                <div>Drag a row into a panel to place it</div>
              </div>
            )}
            {(() => {
              const railRow = (r: KeywordSearchResultItem) => (
                <RailRow
                  key={r.drug_id}
                  usFdaVisible={usFdaVisible}
                  r={r}
                  isCogni={isCogni}
                  starred={stars.includes(r.drug_id)}
                  inCompare={panels.includes(r.drug_id)}
                  atCompareCap={panels.length >= MAX_PANELS}
                  atStarCap={stars.length >= MAX_STARS}
                  onStar={toggleStar}
                  onToggleCompare={togglePanel}
                  accentColor={panelColorOf(r.drug_id)}
                  slotLetter={slotLetterOf(r.drug_id)}
                  selectable={selectMode}
                  selectChecked={selectedIds.has(r.drug_id)}
                  onSelectToggle={onSelectToggle}
                  selectDisabled={atSelectCap && !selectedIds.has(r.drug_id)}
                />
              );
              const groups = railGroups;
              if (!groups) return <ul className="space-y-1">{railItems.map(railRow)}</ul>;
              return (
                <div className="space-y-2">
                  {groups.map((g) => {
                    const isCollapsed = collapsedGroups.has(g.key);
                    return (
                      <div key={g.key}>
                        {/* A JP manufacturer group label is a full postal
                            address; clamp it and keep the whole string in the
                            tooltip rather than letting it eat the rail. */}
                        <button
                          onClick={() => toggleGroup(g.key)}
                          title={g.label}
                          aria-expanded={!isCollapsed}
                          className="flex w-full items-start gap-1 rounded-ctl bg-panel px-1.5 py-1 text-left hover:bg-line-subtle"
                        >
                          <span className="mt-0.5 shrink-0 text-[9px] text-ink-3">
                            {isCollapsed ? '▶' : '▼'}
                          </span>
                          <span
                            className="flex-1 text-[11px] font-semibold leading-tight text-ink-2"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          >
                            {g.label}
                          </span>
                          <span className="shrink-0 rounded-full bg-white px-1.5 text-[10px] font-semibold tabular-nums text-ink-2">
                            {g.rows.length}
                          </span>
                        </button>
                        {!isCollapsed && <ul className="mt-1 space-y-1">{g.rows.map(railRow)}</ul>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {activeTab === 'results' && (isCogni ? cogniLoadingMore : rail.isFetchingNextPage) && <div className="py-2 text-center text-[11px] text-ink-3">Loading more…</div>}
            {activeTab === 'results' && (isCogni
              ? cogniHasMore && !cogniLoadingMore && (
                  <button onClick={() => setCogniPage((p) => p + 1)} className="mt-1 w-full rounded-ctl border border-line py-1 text-center text-[11px] text-accent hover:bg-panel">
                    Load more results
                  </button>
                )
              : rail.hasNextPage && !rail.isFetchingNextPage && (
                  <button onClick={() => rail.fetchNextPage()} className="mt-1 w-full rounded-ctl border border-line py-1 text-center text-[11px] text-accent hover:bg-panel">
                    Load more results
                  </button>
                ))}
            </div>
            {/* Footer: save the shortlist as a list. The save popover is re-anchored to open upward from the left edge (it is
                wider than the rail). */}
            {stars.length > 0 && (
              <div data-testid="rail-starred-footer" className="shrink-0 space-y-2 border-t border-line bg-white p-3">
                <div className="truncate text-xs text-ink-2" title={starFooterText}>{starFooterText}</div>
                <div className="[&_.absolute]:bottom-full [&_.absolute]:left-0 [&_.absolute]:right-auto [&_.absolute]:mb-1 [&_.absolute]:mt-0">
                  <SaveStarredListButton
                    drugs={starredDrugsForSave()}
                    searchContext={savedSearchContext()}
                    defaultName={phrase || 'Starred list'}
                    editListId={editListId}
                    editListName={editListName}
                    preserveContextOnUpdate={editListId != null && kwRequest == null}
                  />
                </div>
              </div>
            )}
          </aside>
        )}

        {/* main: the column strip (up to 100 columns) */}
          <main className="flex min-w-0 flex-1 flex-col p-3">
            {/* Starts at 2 panels (classic A/B); "+ Add panel" grows to 100, each
                new slot EMPTY until filled by clicking / dragging any rail drug.
                Same fixed size (2 fit; the rest scroll right) — "+ Add panel"
                auto-scrolls to reveal the new slot. Identity color per slot;
                filled columns mount lazily (PDF viewers are heavy). */}
            <div ref={panelStripRef} data-testid="panel-strip" className="flex min-h-0 flex-1 gap-3 overflow-x-auto">
              {Array.from({ length: slots }).map((_, slotIndex) => {
                const id = panels[slotIndex];
                const accent = slotColor(slotIndex);
                if (id == null) {
                  // Empty slot — drop target + "pick from the list" prompt. A ✕
                  // removes the slot (only the trailing empties are removable).
                  const empties = panels.length;
                  const isLastEmpty = slotIndex === slots - 1;
                  return (
                    <div
                      key={`empty-${slotIndex}`}
                      className="h-full shrink-0"
                      style={{ width: 'calc(50% - 0.375rem)', minWidth: 360 }}
                      onDragOver={(e) => { if (e.dataTransfer.types.includes('text/plain')) e.preventDefault(); }}
                      onDrop={(e) => {
                        const raw = e.dataTransfer.getData('text/plain');
                        const v = raw.startsWith('col:') ? raw.slice(4) : raw;
                        if (/^\d+$/.test(v)) { e.preventDefault(); replaceSlot(slotIndex, Number(v)); }
                      }}
                    >
                      <div className="flex h-full w-full flex-col overflow-hidden rounded border-2 border-dashed border-line-input">
                        <div className="h-[3px] w-full shrink-0 opacity-40" style={{ backgroundColor: accent }} />
                        <div className="relative flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
                          {(isLastEmpty || empties === 0) && (
                            <button onClick={() => removePanel(slotIndex)} title="Remove this panel" className="absolute right-2 top-2 rounded px-1 text-ink-3 hover:bg-line hover:text-ink-2">✕</button>
                          )}
                          <span data-testid="empty-slot-badge" className="inline-flex h-7 w-7 items-center justify-center rounded-ctl font-mono text-[11px] text-white" style={{ backgroundColor: accent }}>{slotLabel(slotIndex)}</span>
                          <div className="text-sm font-medium text-ink-3">Empty panel</div>
                          <div className="text-xs text-ink-3">Click a drug in the list on the left (or drag one here) to fill it</div>
                        </div>
                      </div>
                    </div>
                  );
                }
                const hl = panelHL(id);
                return (
                  <div key={id} className="h-full shrink-0" style={{ width: 'calc(50% - 0.375rem)', minWidth: 360 }}>
                    <LazyMount
                      className="h-full w-full"
                      placeholder={
                        <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded border border-line bg-panel px-4 text-center">
                          <div className="text-sm font-medium text-ink-3">
                            {resultLookup.find((r) => r.drug_id === id)?.brand_name ?? 'Drug'}
                          </div>
                          <div className="text-xs text-ink-3">Scroll here to load…</div>
                        </div>
                      }
                    >
                    <DrugColumn
                      drugId={id}
                      result={resultLookup.find((r) => r.drug_id === id) ?? null}
                      sectionNumber={section}
                      selectedSections={colSections[id]}
                      concepts={panelConcepts(id)}
                      universalPicks={universalConcepts}
                      onClearSections={() => clearDrugSections(id)}
                      slotLetter={slotLabel(slotIndex)}
                      onSectionsChange={(next) => setDrugSections(id, next)}
                      phrase={hl.phrase}
                      terms={hl.terms}
                      colors={hl.colors}
                      englishExact={!isCogni && isEnglishExactSource(resultLookup.find((r) => r.drug_id === id)?.highlights?.[0]?.source ?? (kwRequest?.sources?.length === 1 ? kwRequest.sources[0] : null))}
                      accentColor={accent}
                    evidenceChunkText={isCogni ? (cogniEvidence.get(id)?.chunkText ?? null) : null}
                    scrollToSectionNumber={isCogni ? (cogniEvidence.get(id)?.section ?? null) : null}
                      onOpen={openDrug}
                      onRemove={() => removePanel(slotIndex)}
                      onDropDrug={(d) => replaceSlot(slotIndex, d)}
                    />
                    </LazyMount>
                  </div>
                );
              })}
              {/* "+ Add panel": appends an empty slot to fill from the rail. Absent at 100. */}
              {canAddPanel && (
                <button
                  data-testid="add-panel-tile"
                  onClick={addPanel}
                  title="Add another panel (up to 100), then fill it from the list"
                  className="flex h-full w-40 shrink-0 flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-line-input text-ink-2 transition-colors hover:bg-accent-tint"
                >
                  <span className="text-sm font-semibold">+ Add panel</span>
                  <span className="text-xs text-ink-3">{slots + 1} of {MAX_PANELS}</span>
                </button>
              )}
            </div>
          </main>
      </div>

      {/* Share dialog — Cogni-style snapshot link */}
      {shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShareUrl(null)}>
          <div className="w-[28rem] max-w-[90vw] rounded bg-white p-5 shadow-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 text-sm font-semibold text-ink">Share this comparison</div>
            {shareUrl === 'error' ? (
              <div className="text-sm text-red-600">Couldn’t create the share link. Please try again.</div>
            ) : shareUrl === 'all-unloadable' ? (
              <div className="text-sm text-warn-ink">
                None of the drugs in this comparison could be loaded — they may have been removed. There’s nothing to share.
              </div>
            ) : (
              <>
                <div className="mb-3 text-xs text-ink-3">
                  Anyone with this link can view a read-only snapshot of this comparison (keyword highlighted) — no login needed.
                </div>
                <div className="flex items-center gap-2">
                  <input readOnly value={shareUrl} className="min-w-0 flex-1 rounded-ctl border border-line-input px-2 py-1 text-xs text-ink-2" onFocus={(e) => e.currentTarget.select()} />
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      } catch {
                        /* clipboard blocked */
                      }
                    }}
                    className="btn-primary shrink-0"
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                {shareWarning && (
                  <div className="mt-3 rounded-ctl border border-warn-line bg-warn-bg px-2.5 py-1.5 text-xs text-warn-ink">
                    {shareWarning}
                  </div>
                )}
              </>
            )}
            <div className="mt-4 text-right">
              <button onClick={() => setShareUrl(null)} className="rounded-ctl px-3 py-1 text-sm text-ink-2 hover:bg-panel">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
